import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { api, getApiErrorMessage, isAuthSessionError } from "../services/api";
import {
  attachPushListeners,
  getNativePushPlatform,
  getPushPermissionState,
  isNativePushRuntime,
  registerPushNotifications,
  registerForPushNotifications,
  requestPushPermissionAccess,
} from "../services/pushNotifications";
import SubscriptionPaywall from "../components/SubscriptionPaywall";
import ListPage from "./dashboardTabs/ListPage";
import MapPage from "./dashboardTabs/MapPage";
import MarketPage from "./dashboardTabs/MarketPage";
import RankingPage from "./dashboardTabs/RankingPage";
import SearchPage from "./dashboardTabs/SearchPage";
import SettingsPage from "./dashboardTabs/SettingsPage";
import {
  LOCATION_DISCOVERY_RADIUS_KM,
  MAP_DISCOVERY_RADIUS_KM_FALLBACK,
  surfaceStyle,
  tabs,
} from "./dashboardStyles";
import {
  buildOptionalAuthConfig,
  extractProductsFromText,
  formatAlternateDistance,
  formatCoordinate,
  formatDistance,
  formatDistanceInputValue,
  getDistanceUnitLabel,
  getRuntimeCurrency,
  hasValidCoordinates,
  normalizePermissionState,
  normalizeTextKey,
  parseDistanceInputToKm,
  parseOptionalNumber,
  resolveMarketDistance,
  setRuntimeCurrencyForLanguage,
  usesImperialDistanceSystem,
} from "./dashboardUtils";
import { createTranslator, getLocaleForLanguage, LANGUAGE_OPTIONS, translateRuntimeText } from "../i18n";

function hasAuthHeaders(headers) {
  return headers && headers.Authorization;
}

const DEFAULT_ADDRESS_LABELS = new Set(["Casa", "Home", "Maison", "Zuhause"]);
const ADDRESS_STREET_TERMS = new Set([
  "ave",
  "avenue",
  "av",
  "avenida",
  "blvd",
  "boulevard",
  "drive",
  "dr",
  "ln",
  "lane",
  "rd",
  "road",
  "rua",
  "st",
  "street",
  "way",
]);

function looksLikeSpecificAddressQuery(value) {
  const normalized = normalizeTextKey(value);
  if (!normalized) {
    return false;
  }
  const words = normalized.split(/\s+/).filter(Boolean);
  return /\d/.test(normalized) || words.some((word) => ADDRESS_STREET_TERMS.has(word)) || words.length > 3;
}

function isApproximateAddressSuggestion(suggestion) {
  if (!suggestion) {
    return false;
  }
  const source = String(suggestion.source || "").toLowerCase();
  const label = normalizeTextKey(suggestion.label || suggestion.address || "");
  return Boolean(suggestion.is_approximate) || source.includes("approximate") || label.includes("aproximado");
}

function inferFallbackMarketCategory(market) {
  const text = normalizeTextKey(
    [market?.market, market?.address, market?.category]
      .filter(Boolean)
      .join(" ")
  );
  if (!text) {
    return "market";
  }
  if (
    /pharmacy|farmacia|drogaria|drugstore|walgreens|cvs|droga raia|drogasil|farmacias/.test(text)
  ) {
    return "pharmacy";
  }
  if (
    /quiktrip|oxxo|ampm|shell|select|convenience|conveniencia|c-store|casey|posto/.test(text)
  ) {
    return "convenience";
  }
  if (
    /atacado|atacadista|warehouse|wholesale|costco|sam s club|sams club|club/.test(text)
  ) {
    return "warehouse";
  }
  if (
    /walmart|aldi|kroger|dillons|carrefour|supermarket|supermercado|mercado|grocery|whole foods|trader joe/.test(
      text
    )
  ) {
    return "supermarket";
  }
  return "market";
}

export default function DashboardShell({
  token: tokenProp,
  onLogout,
  language = "pt-BR",
  setLanguage = () => {},
}) {
  const authToken = tokenProp || localStorage.getItem("token") || "";
  const t = useMemo(() => createTranslator(language), [language]);
  const translateText = useCallback(
    (text, params = {}) => translateRuntimeText(language, text, params),
    [language]
  );
  const authHeaders = useMemo(
    () => (authToken ? { Authorization: `Bearer ${authToken}` } : null),
    [authToken]
  );
  const [authSessionExpired, setAuthSessionExpired] = useState(false);
  const activeAuthHeaders = useMemo(
    () => (authSessionExpired ? null : authHeaders),
    [authHeaders, authSessionExpired]
  );
  const viewportDiscoveryTimeoutRef = useRef(null);
  const searchLookupTimeoutRef = useRef(null);
  const searchResultCacheRef = useRef(new Map());
  const searchSuggestionCacheRef = useRef(new Map());
  const lastViewportSignatureRef = useRef("");
  const latestLocationRequestRef = useRef(0);
  const didAutoLocateRef = useRef(false);
  const hasSyncedDeviceLocationRef = useRef(false);
  const manualLocationOverrideRef = useRef(false);
  const lastMapAutoRefreshKeyRef = useRef("");
  const seenNotificationIdsRef = useRef(new Set());
  const registeredPushTokenRef = useRef("");
  const pushListenerCleanupRef = useRef(null);
  const previousDistanceSystemRef = useRef(usesImperialDistanceSystem({ language }));
  const nextLocationRequestToken = useCallback(() => {
    latestLocationRequestRef.current += 1;
    return latestLocationRequestRef.current;
  }, []);
  const isLatestLocationRequest = useCallback(
    (requestToken) =>
      requestToken === undefined || requestToken === latestLocationRequestRef.current,
    []
  );

  const [activeTab, setActiveTab] = useState("map");
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [statusState, setStatusState] = useState({ message: "", tone: "info" });
  const [profileForm, setProfileForm] = useState({
    full_name: "",
    preferred_language: language,
    discovery_radius_miles: formatDistanceInputValue(LOCATION_DISCOVERY_RADIUS_KM, { language }),
  });
  const [addressQuery, setAddressQuery] = useState("");
  const [selectedAddressLabel, setSelectedAddressLabel] = useState("");
  const [, setAddressSuggestions] = useState([]);
  const [searchProduct, setSearchProduct] = useState("");
  const [unifiedSearchResult, setUnifiedSearchResult] = useState(null);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [isSearchingSuggestions, setIsSearchingSuggestions] = useState(false);
  const [selectedSearchSuggestionLabel, setSelectedSearchSuggestionLabel] = useState("");
  const [selectedProductInsight, setSelectedProductInsight] = useState(null);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("");
  const [selectedBrandFilter, setSelectedBrandFilter] = useState("");
  const [alertTargetPrice, setAlertTargetPrice] = useState("");
  const [alertMaxDistanceKm, setAlertMaxDistanceKm] = useState(() =>
    formatDistanceInputValue(LOCATION_DISCOVERY_RADIUS_KM, { language })
  );
  const [shoppingListText, setShoppingListText] = useState("");
  const [shoppingResult, setShoppingResult] = useState(null);
  const [compareResult, setCompareResult] = useState(null);
  const [routeResult, setRouteResult] = useState(null);
  const [shoppingListAlertTargetTotal, setShoppingListAlertTargetTotal] = useState("");
  const [shoppingListAlertMaxDistanceKm, setShoppingListAlertMaxDistanceKm] = useState(() =>
    formatDistanceInputValue(LOCATION_DISCOVERY_RADIUS_KM, { language })
  );
  const [ranking, setRanking] = useState([]);
  const [marketCatalog, setMarketCatalog] = useState([]);
  const [selectedMarketPrices, setSelectedMarketPrices] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [selectedMarketId, setSelectedMarketId] = useState(null);
  const [selectedMarketSnapshot, setSelectedMarketSnapshot] = useState(null);
  const [selectedMapPoint, setSelectedMapPoint] = useState(null);
  const [selectedMarketProductName, setSelectedMarketProductName] = useState("");
  const [isLocating, setIsLocating] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isSearchingProducts, setIsSearchingProducts] = useState(false);
  const [isRequestingNotifications, setIsRequestingNotifications] = useState(false);
  const [isLoadingMarkets, setIsLoadingMarkets] = useState(false);
  const [isLoadingMarketPrices, setIsLoadingMarketPrices] = useState(false);
  const [marketCatalogSyncMeta, setMarketCatalogSyncMeta] = useState({
    status: "idle",
    provider: "",
    imported: 0,
    skipped: 0,
    message: "",
  });
  const [locationPermission, setLocationPermission] = useState("checking");
  const [notificationPermission, setNotificationPermission] = useState("checking");
  const [autoDiscoveredCount, setAutoDiscoveredCount] = useState(0);
  const [searchLat, setSearchLat] = useState("");
  const [searchLng, setSearchLng] = useState("");
  const [radiusMarketCount, setRadiusMarketCount] = useState(0);
  const [isDiscoveringRadiusMarkets, setIsDiscoveringRadiusMarkets] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [shoppingListAlerts, setShoppingListAlerts] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [billingOverview, setBillingOverview] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [deviceTokens, setDeviceTokens] = useState([]);
  const [adminDashboard, setAdminDashboard] = useState(null);
  const [analyticsOverview, setAnalyticsOverview] = useState(null);
  const [suspiciousPrices, setSuspiciousPrices] = useState([]);
  const [newAddressLabel, setNewAddressLabel] = useState("Casa");
  const [isActivatingSubscription, setIsActivatingSubscription] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteAccountForm, setDeleteAccountForm] = useState({
    current_password: "",
    confirmation_text: "",
  });
  const [selectedCategoryFilters, setSelectedCategoryFilters] = useState([]);
  const [activeCurrency, setActiveCurrency] = useState(() => getRuntimeCurrency());
  const localizedTabs = useMemo(
    () =>
      tabs.map((tab) => ({
        ...tab,
        label: t(`tab.${tab.id}`, tab.label),
      })),
    [t]
  );
  const selectTab = useCallback((tabId) => {
    setActiveTab(tabId);
    setIsNavOpen(false);
  }, []);

  const nativePushRuntime = isNativePushRuntime();
  const supportsBrowserNotifications =
    typeof window !== "undefined" && "Notification" in window;
  const supportsNotifications = nativePushRuntime || supportsBrowserNotifications;
  const secureContextAvailable =
    typeof window === "undefined"
      ? true
      : window.isSecureContext ||
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";

  const userLocation = useMemo(() => {
    const latitude = parseOptionalNumber(searchLat);
    const longitude = parseOptionalNumber(searchLng);
    return hasValidCoordinates(latitude, longitude) ? { latitude, longitude } : null;
  }, [searchLat, searchLng]);

  const distanceUnitLabel = useMemo(() => getDistanceUnitLabel({ language }), [language]);
  const usesImperialDistance = useMemo(
    () => usesImperialDistanceSystem({ language }),
    [language]
  );

  useEffect(() => {
    const nextCurrency = setRuntimeCurrencyForLanguage(language);
    setActiveCurrency(nextCurrency);
  }, [language]);

  useEffect(() => {
    if (hasAuthHeaders(activeAuthHeaders)) {
      return;
    }
    registeredPushTokenRef.current = "";
    setDeviceTokens([]);
  }, [activeAuthHeaders]);

  const configuredDiscoveryRadiusMiles = useMemo(() => {
    const formRadiusKm = parseDistanceInputToKm(profileForm.discovery_radius_miles, { language });
    if (formRadiusKm && formRadiusKm > 0) {
      return formRadiusKm;
    }

    const savedRadiusKm = Number(userProfile?.default_search_radius_km);
    return Number.isFinite(savedRadiusKm) && savedRadiusKm > 0
      ? savedRadiusKm
      : LOCATION_DISCOVERY_RADIUS_KM;
  }, [language, profileForm.discovery_radius_miles, userProfile]);

  const configuredDiscoveryRadiusKm = configuredDiscoveryRadiusMiles;

  const configuredDiscoveryRadiusMilesLabel = useMemo(
    () =>
      formatDistanceInputValue(configuredDiscoveryRadiusKm, { language }) ||
      formatDistanceInputValue(LOCATION_DISCOVERY_RADIUS_KM, { language }),
    [configuredDiscoveryRadiusKm, language]
  );

  const configuredDiscoveryRadiusKmLabel = useMemo(
    () => formatDistance(configuredDiscoveryRadiusKm, { language }),
    [configuredDiscoveryRadiusKm, language]
  );

  const configuredDiscoveryRadiusSecondaryLabel = useMemo(
    () => formatAlternateDistance(configuredDiscoveryRadiusKm, { language }),
    [configuredDiscoveryRadiusKm, language]
  );

  const defaultDiscoveryRadiusLabel = useMemo(
    () => formatDistanceInputValue(LOCATION_DISCOVERY_RADIUS_KM, { language }),
    [language]
  );

  useEffect(() => {
    const previousUsesImperial = previousDistanceSystemRef.current;
    if (previousUsesImperial === usesImperialDistance) {
      setProfileForm((current) =>
        current.preferred_language === language
          ? current
          : { ...current, preferred_language: language }
      );
      return;
    }

    setProfileForm((current) => {
      const currentRadiusKm =
        parseDistanceInputToKm(current.discovery_radius_miles, {
          useImperial: previousUsesImperial,
        }) || LOCATION_DISCOVERY_RADIUS_KM;
      return {
        ...current,
        preferred_language: language,
        discovery_radius_miles: formatDistanceInputValue(currentRadiusKm, { language }),
      };
    });
    setAlertMaxDistanceKm((current) =>
      formatDistanceInputValue(
        parseDistanceInputToKm(current, { useImperial: previousUsesImperial }) ||
          configuredDiscoveryRadiusKm,
        { language }
      )
    );
    setShoppingListAlertMaxDistanceKm((current) =>
      formatDistanceInputValue(
        parseDistanceInputToKm(current, { useImperial: previousUsesImperial }) ||
          configuredDiscoveryRadiusKm,
        { language }
      )
    );
    previousDistanceSystemRef.current = usesImperialDistance;
  }, [configuredDiscoveryRadiusKm, language, usesImperialDistance]);

  useEffect(() => {
    setAlertMaxDistanceKm(configuredDiscoveryRadiusMilesLabel);
  }, [configuredDiscoveryRadiusMilesLabel, selectedProductInsight?.product_id]);

  useEffect(() => {
    setShoppingListAlertMaxDistanceKm(configuredDiscoveryRadiusMilesLabel);
  }, [configuredDiscoveryRadiusMilesLabel, shoppingResult?.shopping_list_id]);

  useEffect(() => {
    setNewAddressLabel((current) =>
      DEFAULT_ADDRESS_LABELS.has(String(current || "").trim())
        ? t("settings.addresses.default_label", "Casa")
        : current
    );
  }, [t]);

  const favoriteMarketIds = useMemo(
    () =>
      new Set(
        favorites
          .filter((item) => item.entity_type === "market")
          .map((item) => Number(item.entity_id))
          .filter(Number.isFinite)
      ),
    [favorites]
  );

  const defaultAddressId = useMemo(
    () => savedAddresses.find((item) => item.is_default)?.id || null,
    [savedAddresses]
  );

  const selectedMarket =
    marketCatalog.find((market) => market.market_id === selectedMarketId) ||
    (selectedMarketSnapshot?.market_id === selectedMarketId ? selectedMarketSnapshot : null);

  const selectedMarketFavorite = selectedMarket ? favoriteMarketIds.has(selectedMarket.market_id) : false;

  const availableMarketCategories = useMemo(
    () =>
      [...new Set(
        marketCatalog
          .map((market) => market.category || inferFallbackMarketCategory(market))
          .filter(Boolean)
      )]
        .sort((left, right) => String(left).localeCompare(String(right))),
    [marketCatalog]
  );

  const mappedMarkets = useMemo(
    () =>
      marketCatalog
        .filter((market) => hasValidCoordinates(market.latitude, market.longitude))
        .map((market) => ({
          ...market,
          category: market.category || inferFallbackMarketCategory(market),
          favorite: favoriteMarketIds.has(market.market_id),
          resolvedDistanceKm: resolveMarketDistance(market, userLocation),
        }))
        .filter(
          (market) =>
            !userLocation ||
            (market.resolvedDistanceKm !== null &&
              market.resolvedDistanceKm <= configuredDiscoveryRadiusKm)
        ),
    [configuredDiscoveryRadiusKm, favoriteMarketIds, marketCatalog, userLocation]
  );

  const categoryFilteredMarkets = useMemo(
    () =>
      selectedCategoryFilters.length === 0
        ? mappedMarkets
        : mappedMarkets.filter((market) => selectedCategoryFilters.includes(market.category || "")),
    [mappedMarkets, selectedCategoryFilters]
  );

  useEffect(() => {
    if (selectedCategoryFilters.length === 0) {
      return;
    }
    setSelectedCategoryFilters((current) => {
      const nextFilters = current.filter((category) => availableMarketCategories.includes(category));
      return nextFilters.length === current.length ? current : nextFilters;
    });
  }, [availableMarketCategories, selectedCategoryFilters.length]);

  const distanceRanking = useMemo(
    () =>
      categoryFilteredMarkets
        .filter((market) => market.resolvedDistanceKm !== null)
        .sort((left, right) => left.resolvedDistanceKm - right.resolvedDistanceKm),
    [categoryFilteredMarkets]
  );

  const localPriceRanking = useMemo(() => {
    if (!userLocation) {
      return [];
    }
    return ranking
      .map((market) => ({
        ...market,
        resolvedDistanceKm: resolveMarketDistance(market, userLocation),
      }))
      .filter(
        (market) =>
          market.resolvedDistanceKm !== null &&
          market.resolvedDistanceKm <= configuredDiscoveryRadiusKm
      )
      .sort((left, right) => {
        const leftIndex = Number(left.price_index);
        const rightIndex = Number(right.price_index);
        return (
          (Number.isFinite(leftIndex) ? leftIndex : 10 ** 9) -
            (Number.isFinite(rightIndex) ? rightIndex : 10 ** 9) ||
          left.resolvedDistanceKm - right.resolvedDistanceKm
        );
      });
  }, [configuredDiscoveryRadiusKm, ranking, userLocation]);

  const visibleMarkets = useMemo(
    () => (distanceRanking.length > 0 ? distanceRanking.slice(0, 10) : categoryFilteredMarkets.slice(0, 10)),
    [categoryFilteredMarkets, distanceRanking]
  );

  const selectedMarketRanking = useMemo(
    () => localPriceRanking.find((entry) => entry.market_id === selectedMarket?.market_id) || null,
    [localPriceRanking, selectedMarket]
  );

  const selectedMarketInsight = useMemo(() => {
    if (!selectedMarket) {
      return null;
    }

    const distanceKm = resolveMarketDistance(selectedMarket, userLocation);
    const trackedProducts =
      selectedMarketPrices.length || Number(selectedMarketRanking?.tracked_products) || 0;
    const contributions =
      selectedMarketPrices.reduce(
        (total, entry) => total + (Number(entry.contributions) || 0),
        0
      ) ||
      Number(selectedMarketRanking?.contributions) ||
      0;
    const priceIndex = Number(selectedMarketRanking?.price_index);
    const estimatedRating = Number.isFinite(priceIndex)
      ? Math.max(3, Math.min(5, 4.8 - (priceIndex - 1) * 1.1 + Math.min(contributions / 50, 0.35)))
      : trackedProducts > 0
        ? 4.2
        : null;
    const reliabilityLabel =
      contributions >= 24 || trackedProducts >= 14
        ? "Alta"
        : contributions >= 10 || trackedProducts >= 6
          ? "Media"
          : trackedProducts > 0
            ? "Inicial"
            : "Nova";
    const reliabilityTone =
      reliabilityLabel === "Alta"
        ? "granted"
        : reliabilityLabel === "Media"
          ? "prompt"
          : "unsupported";
    const freshestUpdateValue = selectedMarketPrices.reduce((latest, entry) => {
      const candidateTime = new Date(entry.captured_at || "").getTime();
      if (Number.isNaN(candidateTime)) {
        return latest;
      }
      if (!latest || candidateTime > latest) {
        return candidateTime;
      }
      return latest;
    }, 0);

    return {
      distanceKm,
      distanceLabel: formatDistance(distanceKm),
      ratingLabel: estimatedRating ? `${estimatedRating.toFixed(1)}/5` : "n/d",
      reliabilityLabel,
      reliabilityTone,
      productsCount: trackedProducts,
      contributions,
      priceIndexLabel: Number.isFinite(priceIndex) ? priceIndex.toFixed(3) : null,
      freshestUpdateLabel: freshestUpdateValue
        ? new Date(freshestUpdateValue).toLocaleString(getLocaleForLanguage(language), {
            dateStyle: "short",
            timeStyle: "short",
          })
        : "n/d",
    };
  }, [language, selectedMarket, selectedMarketPrices, selectedMarketRanking, userLocation]);

  const selectedMarketProduct = useMemo(() => {
    if (selectedMarketPrices.length === 0) {
      return null;
    }
    const normalizedSelectedProduct = normalizeTextKey(selectedMarketProductName);
    if (!normalizedSelectedProduct) {
      return selectedMarketPrices[0];
    }
    return (
      selectedMarketPrices.find(
        (entry) => normalizeTextKey(entry.product) === normalizedSelectedProduct
      ) || selectedMarketPrices[0]
    );
  }, [selectedMarketPrices, selectedMarketProductName]);

  const setStatus = useCallback(
    (message, tone = "info") => {
      setStatusState({ message: translateText(message), tone });
    },
    [translateText]
  );

  const invalidateExpiredSession = useCallback(
    (message = "Sua sessao expirou. Entre novamente para salvar alteracoes da conta.") => {
      setAuthSessionExpired(true);
      try {
        localStorage.removeItem("token");
      } catch (error) {
        // Ignore storage cleanup issues and keep the current screen responsive.
      }
      registeredPushTokenRef.current = "";
      setUserProfile(null);
      setFavorites([]);
      setAlerts([]);
      setShoppingListAlerts([]);
      setSavedAddresses([]);
      setBillingOverview(null);
      setNotifications([]);
      setDeviceTokens([]);
      setAdminDashboard(null);
      setAnalyticsOverview(null);
      setSuspiciousPrices([]);
      setStatus(message, "warn");
    },
    [setStatus]
  );

  const setProfileValue = (field, value) => {
    setProfileForm((current) => ({ ...current, [field]: value }));
  };

  const setDeleteAccountValue = (field, value) => {
    setDeleteAccountForm((current) => ({ ...current, [field]: value }));
  };

  const toggleMapCategory = useCallback((category) => {
    if (!category) {
      setSelectedCategoryFilters([]);
      return;
    }
    setSelectedCategoryFilters((current) =>
      current.includes(category)
        ? current.filter((item) => item !== category)
        : [...current, category]
    );
  }, []);

  const syncDisplayedAddress = useCallback((label) => {
    const normalizedLabel = String(label || "").trim();
    setAddressQuery(normalizedLabel);
    setSelectedAddressLabel(normalizedLabel);
    setAddressSuggestions([]);
  }, []);

  const loadDeviceTokens = useCallback(async () => {
    if (!hasAuthHeaders(activeAuthHeaders)) {
      setDeviceTokens([]);
      return [];
    }
    try {
      const response = await api.get("/users/me/device-tokens", { headers: activeAuthHeaders });
      const rows = response.data || [];
      setDeviceTokens(rows);
      return rows;
    } catch (error) {
      if (isAuthSessionError(error)) {
        invalidateExpiredSession();
      }
      return [];
    }
  }, [activeAuthHeaders, invalidateExpiredSession]);

  const revokeCurrentDeviceToken = useCallback(async () => {
    if (!hasAuthHeaders(activeAuthHeaders)) {
      return false;
    }
    const tokenValue = String(registeredPushTokenRef.current || "").trim();
    if (!tokenValue) {
      return false;
    }
    try {
      await api.post(
        "/users/me/device-tokens/revoke",
        { token: tokenValue },
        { headers: activeAuthHeaders }
      );
      registeredPushTokenRef.current = "";
      await loadDeviceTokens();
      return true;
    } catch (error) {
      if (isAuthSessionError(error)) {
        invalidateExpiredSession();
      }
      return false;
    }
  }, [activeAuthHeaders, invalidateExpiredSession, loadDeviceTokens]);

  const logout = useCallback(async () => {
    await revokeCurrentDeviceToken();
    localStorage.removeItem("token");
    if (typeof onLogout === "function") {
      onLogout();
      return;
    }
    window.location.reload();
  }, [onLogout, revokeCurrentDeviceToken]);

  const loadFavorites = useCallback(async () => {
    if (!hasAuthHeaders(activeAuthHeaders)) {
      setFavorites([]);
      return;
    }
    try {
      const response = await api.get("/users/me/favorites", { headers: activeAuthHeaders });
      setFavorites(response.data || []);
    } catch (error) {
      if (isAuthSessionError(error)) {
        invalidateExpiredSession();
      }
    }
  }, [activeAuthHeaders, invalidateExpiredSession]);

  const loadAlerts = useCallback(async () => {
    if (!hasAuthHeaders(activeAuthHeaders)) {
      setAlerts([]);
      return;
    }
    try {
      const response = await api.get("/users/me/alerts", { headers: activeAuthHeaders });
      setAlerts(response.data || []);
    } catch (error) {
      if (isAuthSessionError(error)) {
        invalidateExpiredSession();
      }
    }
  }, [activeAuthHeaders, invalidateExpiredSession]);

  const loadShoppingListAlerts = useCallback(async () => {
    if (!hasAuthHeaders(activeAuthHeaders)) {
      setShoppingListAlerts([]);
      return;
    }
    try {
      const response = await api.get("/users/me/shopping-list-alerts", { headers: activeAuthHeaders });
      setShoppingListAlerts(response.data || []);
    } catch (error) {
      if (isAuthSessionError(error)) {
        invalidateExpiredSession();
      }
    }
  }, [activeAuthHeaders, invalidateExpiredSession]);

  const loadSavedAddresses = useCallback(async () => {
    if (!hasAuthHeaders(activeAuthHeaders)) {
      setSavedAddresses([]);
      return;
    }
    try {
      const response = await api.get("/users/me/addresses", { headers: activeAuthHeaders });
      setSavedAddresses(response.data || []);
    } catch (error) {
      if (isAuthSessionError(error)) {
        invalidateExpiredSession();
      }
    }
  }, [activeAuthHeaders, invalidateExpiredSession]);

  const loadBillingOverview = useCallback(async () => {
    if (!hasAuthHeaders(activeAuthHeaders)) {
      setBillingOverview(null);
      return null;
    }
    try {
      const response = await api.get("/billing/overview", { headers: activeAuthHeaders });
      const nextOverview = response.data || null;
      setBillingOverview(nextOverview);
      return nextOverview;
    } catch (error) {
      if (isAuthSessionError(error)) {
        invalidateExpiredSession();
      }
      return null;
    }
  }, [activeAuthHeaders, invalidateExpiredSession]);

  const activateSubscription = useCallback(async () => {
    if (!hasAuthHeaders(activeAuthHeaders)) {
      setStatus("Entre na conta para ativar a assinatura.", "warn");
      return;
    }

    setIsActivatingSubscription(true);
    try {
      const platform = Capacitor?.getPlatform?.() || "web";
      const response = await api.post(
        "/billing/activate",
        {
          provider: "sandbox",
          platform,
          latitude: userLocation?.latitude,
          longitude: userLocation?.longitude,
          address: selectedAddressLabel || undefined,
          country_code: billingOverview?.price_preview?.country_code || undefined,
          provider_transaction_id: `sandbox-${Date.now()}`,
        },
        { headers: activeAuthHeaders }
      );
      setBillingOverview(response.data || null);
      setStatus("Assinatura ativada com sucesso.", "success");
      window.location.reload();
    } catch (error) {
      if (isAuthSessionError(error)) {
        invalidateExpiredSession();
        return;
      }
      setStatus(getApiErrorMessage(error, "Erro ao ativar assinatura."), "error");
    } finally {
      setIsActivatingSubscription(false);
    }
  }, [activeAuthHeaders, billingOverview, invalidateExpiredSession, selectedAddressLabel, setStatus, userLocation]);

  const loadNotifications = useCallback(async ({ silent = true } = {}) => {
    if (!hasAuthHeaders(activeAuthHeaders)) {
      setNotifications([]);
      return [];
    }
    try {
      const response = await api.get("/users/me/notifications", { headers: activeAuthHeaders });
      const nextNotifications = response.data || [];
      setNotifications(nextNotifications);
      nextNotifications.forEach((notification) => {
        if (
          !notification.is_read &&
          notificationPermission === "granted" &&
          !seenNotificationIdsRef.current.has(notification.id) &&
          supportsBrowserNotifications
        ) {
          seenNotificationIdsRef.current.add(notification.id);
          try {
            const browserNotification = new window.Notification(notification.title, {
              body: notification.body,
              icon: "/logo192.png",
              badge: "/logo192.png",
            });
            window.setTimeout(() => browserNotification.close(), 5000);
          } catch (error) {}
        }
      });
      return nextNotifications;
    } catch (error) {
      if (isAuthSessionError(error)) {
        invalidateExpiredSession();
        return [];
      }
      if (!silent) {
        setStatus(getApiErrorMessage(error, "Erro ao carregar notificacoes."), "error");
      }
      return [];
    }
  }, [activeAuthHeaders, invalidateExpiredSession, notificationPermission, setStatus, supportsBrowserNotifications]);

  const loadAdminData = useCallback(async () => {
    if (
      !hasAuthHeaders(activeAuthHeaders) ||
      !userProfile?.is_admin ||
      (billingOverview && !billingOverview.access_granted)
    ) {
      setAdminDashboard(null);
      setAnalyticsOverview(null);
      setSuspiciousPrices([]);
      return;
    }
    try {
      const dashboardResponse = await api.get("/admin/dashboard", { headers: activeAuthHeaders });
      const analyticsResponse = await api.get("/analytics/overview", { headers: activeAuthHeaders });
      const suspiciousResponse = await api.get("/admin/suspicious-prices", { headers: activeAuthHeaders });
      setAdminDashboard(dashboardResponse.data || null);
      setAnalyticsOverview(analyticsResponse.data || null);
      setSuspiciousPrices(suspiciousResponse.data || []);
    } catch (error) {
      if (isAuthSessionError(error)) {
        invalidateExpiredSession();
      }
    }
  }, [activeAuthHeaders, billingOverview, invalidateExpiredSession, userProfile?.is_admin]);

  const toggleFavorite = useCallback(async (payload) => {
    if (!hasAuthHeaders(activeAuthHeaders)) {
      setStatus("Entre na conta para salvar favoritos.", "warn");
      return;
    }
    try {
      const response = await api.post("/users/me/favorites/toggle", payload, {
        headers: activeAuthHeaders,
      });
      const isActive = Boolean(response.data?.active);
      if (payload.entity_type === "product" && Number(payload.entity_id) === selectedProductInsight?.product_id) {
        setSelectedProductInsight((current) =>
          current ? { ...current, favorite: isActive } : current
        );
      }
      setUnifiedSearchResult((current) => {
        if (!current) {
          return current;
        }
        return {
          ...current,
          markets: (current.markets || []).map((item) =>
            payload.entity_type === "market" && item.market_id === Number(payload.entity_id)
              ? { ...item, favorite: isActive }
              : item
          ),
          products: (current.products || []).map((item) =>
            payload.entity_type === "product" && item.product_id === Number(payload.entity_id)
              ? { ...item, favorite: isActive }
              : item
          ),
        };
      });
      await loadFavorites();
      setStatus(
        isActive ? "Favorito salvo." : "Favorito removido.",
        "success"
      );
    } catch (error) {
      setStatus(getApiErrorMessage(error, "Erro ao atualizar favorito."), "error");
    }
  }, [activeAuthHeaders, loadFavorites, selectedProductInsight?.product_id, setStatus]);

  const removeFavorite = useCallback(async (favoriteId) => {
    if (!hasAuthHeaders(activeAuthHeaders)) {
      return;
    }
    try {
      await api.delete(`/users/me/favorites/${favoriteId}`, { headers: activeAuthHeaders });
      await loadFavorites();
    } catch (error) {
      setStatus(getApiErrorMessage(error, "Erro ao remover favorito."), "error");
    }
  }, [activeAuthHeaders, loadFavorites, setStatus]);

  const refreshPermissionStates = useCallback(async () => {
    const nextLocationPermission = navigator.geolocation ? "prompt" : "unsupported";
    const nextNotificationPermission = await getPushPermissionState();
    setLocationPermission(nextLocationPermission);
    setNotificationPermission(nextNotificationPermission);

    if (!navigator.permissions?.query) {
      return;
    }

    try {
      const geolocationPermission = await navigator.permissions.query({ name: "geolocation" });
      setLocationPermission(normalizePermissionState(geolocationPermission.state, nextLocationPermission));
    } catch (error) {}

    if (nativePushRuntime || !supportsBrowserNotifications) {
      return;
    }

    try {
      const notificationsPermission = await navigator.permissions.query({ name: "notifications" });
      setNotificationPermission(
        normalizePermissionState(
          notificationsPermission.state,
          normalizePermissionState(window.Notification.permission, "prompt")
        )
      );
    } catch (error) {}
  }, [nativePushRuntime, supportsBrowserNotifications]);

  const sendActivationNotification = useCallback(
    (title, body) => {
      if (!supportsBrowserNotifications || notificationPermission !== "granted") {
        return;
      }
      try {
        const notification = new window.Notification(title, {
          body,
          icon: "/logo192.png",
          badge: "/logo192.png",
        });
        window.setTimeout(() => notification.close(), 5000);
      } catch (error) {}
    },
    [notificationPermission, supportsBrowserNotifications]
  );

  const loadMarketRankings = useCallback(async (silent = true, options = {}) => {
    const rankingLatitude = options.latitude ?? userLocation?.latitude;
    const rankingLongitude = options.longitude ?? userLocation?.longitude;
    const rankingRadiusKm = options.radiusKm ?? configuredDiscoveryRadiusKm;
    const requestToken = options.requestToken;
    const params = {};
    if (hasValidCoordinates(rankingLatitude, rankingLongitude)) {
      params.user_latitude = rankingLatitude;
      params.user_longitude = rankingLongitude;
      params.radius_km = rankingRadiusKm;
    }
    try {
      const response = await api.get("/rankings/markets", {
        params,
        headers: activeAuthHeaders || undefined,
      });
      if (!isLatestLocationRequest(requestToken)) {
        return response.data || [];
      }
      setRanking(response.data || []);
      if (!silent) {
        setStatus(t("ranking.status.updated", "Ranking de mercados atualizado."), "success");
      }
      return response.data || [];
    } catch (error) {
      if (isAuthSessionError(error) && hasAuthHeaders(activeAuthHeaders)) {
        invalidateExpiredSession();
        try {
          const retryResponse = await api.get("/rankings/markets", { params });
          if (!isLatestLocationRequest(requestToken)) {
            return retryResponse.data || [];
          }
          setRanking(retryResponse.data || []);
          return retryResponse.data || [];
        } catch (retryError) {
          if (!silent && isLatestLocationRequest(requestToken)) {
            setStatus(getApiErrorMessage(retryError, "Erro ao atualizar ranking."), "error");
          }
          return [];
        }
      }
      if (!silent && isLatestLocationRequest(requestToken)) {
        setStatus(getApiErrorMessage(error, "Erro ao atualizar ranking."), "error");
      }
      return [];
    }
  }, [activeAuthHeaders, configuredDiscoveryRadiusKm, invalidateExpiredSession, isLatestLocationRequest, setStatus, t, userLocation]);

  const loadMarkets = useCallback(async ({
    latitude,
    longitude,
    radiusKm,
    silent = false,
    requestToken,
    autoDiscover = false,
    limit = 50,
  } = {}) => {
    const hasCoordinates = latitude !== undefined && longitude !== undefined;
    setIsLoadingMarkets(true);
    try {
      const response = await api.get("/markets", {
        params: hasCoordinates
          ? { latitude, longitude, radius_km: radiusKm, limit, auto_discover: autoDiscover }
          : { limit, auto_discover: autoDiscover },
      });
      if (isLatestLocationRequest(requestToken)) {
        setMarketCatalog(response.data || []);
      }
      if (hasCoordinates && radiusKm && isLatestLocationRequest(requestToken)) {
        setRadiusMarketCount((response.data || []).length);
      }
      return response.data || [];
    } catch (error) {
      if (!silent && isLatestLocationRequest(requestToken)) {
        setStatus(getApiErrorMessage(error, "Erro ao carregar mercados."), "error");
      }
      return [];
    } finally {
      if (isLatestLocationRequest(requestToken)) {
        setIsLoadingMarkets(false);
      }
    }
  }, [isLatestLocationRequest, setStatus]);

  const refreshLocationMarketData = useCallback(async ({ latitude, longitude, radiusKm, silent = true, requestToken } = {}) => {
    if (!hasValidCoordinates(latitude, longitude)) {
      return null;
    }
    try {
      const response = await api.post("/markets/refresh-location", null, {
        params: {
          latitude,
          longitude,
          radius_km: radiusKm,
          limit: 200,
        },
        timeout: 120000,
      });
      const markets = response.data?.markets || [];
      if (isLatestLocationRequest(requestToken)) {
        setAutoDiscoveredCount(Number(response.data?.markets_discovered || markets.length || 0));
        setRadiusMarketCount(markets.length);
      }
      if (markets.length > 0 && isLatestLocationRequest(requestToken)) {
        setMarketCatalog(markets);
      }
      return {
        markets,
        summary: response.data || {},
      };
    } catch (error) {
      if (!silent) {
        setStatus(
          getApiErrorMessage(error, "Nao foi possivel atualizar lojas e catalogos deste endereco agora."),
          "error"
        );
      }
      return null;
    }
  }, [isLatestLocationRequest, setStatus]);

  const discoverMarketsAroundArea = useCallback(async ({ latitude, longitude, radiusKm, silent = false, requestToken } = {}) => {
    if (!hasValidCoordinates(latitude, longitude)) {
      return [];
    }
    try {
      const response = await api.get("/markets/discover", {
        params: { latitude, longitude, radius_km: radiusKm },
      });
      if (isLatestLocationRequest(requestToken)) {
        setAutoDiscoveredCount(response.data.length);
      }
      if (!silent && response.data.length > 0 && isLatestLocationRequest(requestToken)) {
        setStatus(
          `${response.data.length} mercados, supermercados, farmacias e conveniencias foram puxados automaticamente em um raio de ${configuredDiscoveryRadiusKmLabel}.`,
          "success"
        );
      }
      return response.data || [];
    } catch (error) {
      if (!silent) {
        setStatus(
          getApiErrorMessage(error, "Nao foi possivel consultar mercados reais automaticamente agora."),
          "error"
        );
      }
      return [];
    }
  }, [configuredDiscoveryRadiusKmLabel, isLatestLocationRequest, setStatus]);

  const discoverMarketsWithinRadius = useCallback(async ({ latitude, longitude, radiusKm, silent = false, requestToken } = {}) => {
    if (!hasValidCoordinates(latitude, longitude)) {
      return { markets: [], seededCount: 0 };
    }
    const effectiveRadiusKm = radiusKm || configuredDiscoveryRadiusKm;
    if (isLatestLocationRequest(requestToken)) {
      setIsDiscoveringRadiusMarkets(true);
    }
    try {
      const markets = await discoverMarketsAroundArea({
        latitude,
        longitude,
        radiusKm: effectiveRadiusKm,
        silent: true,
        requestToken,
      });
      if (isLatestLocationRequest(requestToken)) {
        setRadiusMarketCount(markets.length);
        setAutoDiscoveredCount(markets.length);
      }
      if (!silent && isLatestLocationRequest(requestToken)) {
        setStatus(
          `${markets.length} locais reais foram sincronizados em um raio de ${configuredDiscoveryRadiusKmLabel} da sua localizacao.`,
          "success"
        );
      }
      return {
        markets,
        seededCount: markets.length,
      };
    } catch (error) {
      if (!silent) {
        setStatus(
          getApiErrorMessage(error, "Nao foi possivel sincronizar os mercados e lojas do seu raio agora."),
          "error"
        );
      }
      return { markets: [], seededCount: 0 };
    } finally {
      if (isLatestLocationRequest(requestToken)) {
        setIsDiscoveringRadiusMarkets(false);
      }
    }
  }, [configuredDiscoveryRadiusKm, configuredDiscoveryRadiusKmLabel, discoverMarketsAroundArea, isLatestLocationRequest, setStatus]);

  const refreshMarketsAfterDiscovery = useCallback(async ({
    latitude,
    longitude,
    radiusKm,
    referenceLatitude,
    referenceLongitude,
    silent = true,
    successMessage = "Mapa de mercados atualizado.",
    requestToken,
    autoDiscover = true,
  } = {}) => {
    if (!hasValidCoordinates(latitude, longitude)) {
      return [];
    }

    const effectiveRadiusKm = radiusKm || configuredDiscoveryRadiusKm;
    const referenceHasCoordinates = hasValidCoordinates(referenceLatitude, referenceLongitude);
    const resolvedReferenceLatitude = referenceHasCoordinates ? referenceLatitude : latitude;
    const resolvedReferenceLongitude = referenceHasCoordinates ? referenceLongitude : longitude;

    const locationRefresh = await refreshLocationMarketData({
      latitude,
      longitude,
      radiusKm: effectiveRadiusKm,
      silent: true,
      requestToken,
    });
    const discovery = locationRefresh
      ? {
          markets: locationRefresh.markets,
          seededCount: Number(locationRefresh.summary?.markets_discovered || locationRefresh.markets.length || 0),
          importedPriceCount: Number(locationRefresh.summary?.imported_price_count || 0),
        }
      : await discoverMarketsWithinRadius({
        latitude,
        longitude,
        radiusKm: effectiveRadiusKm,
        silent: true,
        requestToken,
      });
    const refreshedMarkets = await loadMarkets({
      latitude: resolvedReferenceLatitude,
      longitude: resolvedReferenceLongitude,
      radiusKm: effectiveRadiusKm,
      silent: true,
      requestToken,
      autoDiscover,
    });
    await loadMarketRankings(true, {
      latitude: resolvedReferenceLatitude,
      longitude: resolvedReferenceLongitude,
      radiusKm: effectiveRadiusKm,
      requestToken,
    });

    if (!silent && isLatestLocationRequest(requestToken)) {
      const importedText =
        discovery.importedPriceCount > 0 ? ` ${discovery.importedPriceCount} precos foram atualizados.` : "";
      setStatus(
        `${successMessage}${discovery.seededCount > 0 ? ` ${discovery.seededCount} locais foram importados.` : ""}${importedText}`,
        "success"
      );
    }

    return refreshedMarkets;
  }, [
    configuredDiscoveryRadiusKm,
    discoverMarketsWithinRadius,
    isLatestLocationRequest,
    loadMarketRankings,
    loadMarkets,
    refreshLocationMarketData,
    setStatus,
  ]);

  const refreshMarketDataset = useCallback(async ({
    discoveryLatitude,
    discoveryLongitude,
    radiusKm,
    referenceLatitude,
    referenceLongitude,
    silent = false,
    successMessage = "Mapa de mercados atualizado.",
    requestToken,
    autoDiscover = true,
  } = {}) => {
    const markets = await loadMarkets({
      latitude: hasValidCoordinates(referenceLatitude, referenceLongitude) ? referenceLatitude : discoveryLatitude,
      longitude: hasValidCoordinates(referenceLatitude, referenceLongitude) ? referenceLongitude : discoveryLongitude,
      radiusKm,
      silent: true,
      requestToken,
      autoDiscover,
    });
    if (!silent && isLatestLocationRequest(requestToken)) {
      setStatus(
        `${successMessage} Mostrando os mercados salvos enquanto buscamos novos locais em segundo plano.`,
        "success"
      );
    }
    if (hasValidCoordinates(discoveryLatitude, discoveryLongitude)) {
      const discoveryPromise = refreshMarketsAfterDiscovery({
        latitude: discoveryLatitude,
        longitude: discoveryLongitude,
        radiusKm,
        referenceLatitude,
        referenceLongitude,
        silent: true,
        successMessage,
        requestToken,
        autoDiscover,
      });
      if (markets.length === 0) {
        return await discoveryPromise;
      }
    }
    return markets;
  }, [isLatestLocationRequest, loadMarkets, refreshMarketsAfterDiscovery, setStatus]);

  const resolveAddressFromCoordinates = useCallback(async ({
    latitude,
    longitude,
    fallbackLabel = "",
    silent = true,
    requestToken,
  } = {}) => {
    if (!hasValidCoordinates(latitude, longitude)) {
      return null;
    }

    try {
      const response = await api.get("/locations/reverse", {
        params: { latitude, longitude },
      });
      const nextLabel =
        response.data?.label || response.data?.address || String(fallbackLabel || "").trim();
      if (nextLabel && isLatestLocationRequest(requestToken)) {
        syncDisplayedAddress(nextLabel);
      }
      return response.data || null;
    } catch (error) {
      const nextLabel = String(fallbackLabel || "").trim();
      if (nextLabel && isLatestLocationRequest(requestToken)) {
        syncDisplayedAddress(nextLabel);
      } else if (!silent) {
        setStatus(getApiErrorMessage(error, "Nao foi possivel carregar o endereco agora."), "warn");
      }
      return null;
    }
  }, [isLatestLocationRequest, setStatus, syncDisplayedAddress]);

  const syncUserLocation = useCallback(async ({
    latitude,
    longitude,
    persistProfile = true,
    silent = false,
    successMessage = "Localizacao atualizada com sucesso.",
    addressLabel = "",
    markPermissionGranted = true,
    notifyNearestMarket = true,
    resolveAddress = true,
    source = "manual",
  }) => {
    const isAutomaticSource = source === "auto" || source === "startup";
    if (isAutomaticSource && manualLocationOverrideRef.current) {
      return [];
    }
    const requestToken = nextLocationRequestToken();
    const normalizedLatitude = Number(latitude);
    const normalizedLongitude = Number(longitude);
    if (!hasValidCoordinates(normalizedLatitude, normalizedLongitude)) {
      setStatus("Nao foi possivel entender as coordenadas do dispositivo.", "error");
      return [];
    }
    setSearchLat(formatCoordinate(normalizedLatitude));
    setSearchLng(formatCoordinate(normalizedLongitude));
    setSelectedMarketId(null);
    setSelectedMarketSnapshot(null);
    setSelectedMapPoint(null);
    setSelectedMarketPrices([]);
    setSelectedMarketProductName("");
    if (addressLabel) {
      syncDisplayedAddress(addressLabel);
    }
    if (markPermissionGranted) {
      setLocationPermission("granted");
      hasSyncedDeviceLocationRef.current = true;
    }
    try {
      const discoveryRadiusKm = configuredDiscoveryRadiusKm;
      if (persistProfile && hasAuthHeaders(activeAuthHeaders)) {
        try {
          const profileResponse = await api.patch(
            "/users/me/location",
            {
              default_latitude: normalizedLatitude,
              default_longitude: normalizedLongitude,
              default_search_radius_km: discoveryRadiusKm,
            },
            { headers: activeAuthHeaders }
          );
          setUserProfile(profileResponse.data);
        } catch (error) {
          if (isAuthSessionError(error)) {
            invalidateExpiredSession(
              "Sua sessao expirou. O mapa foi atualizado, mas voce precisa entrar novamente para salvar sua localizacao."
            );
          } else {
            throw error;
          }
        }
      }
      const refreshedMarkets = await refreshMarketDataset({
        discoveryLatitude: normalizedLatitude,
        discoveryLongitude: normalizedLongitude,
        radiusKm: discoveryRadiusKm,
        referenceLatitude: normalizedLatitude,
        referenceLongitude: normalizedLongitude,
        silent: true,
        successMessage: "Endereco atualizado.",
        requestToken,
        autoDiscover: true,
      });
      await loadMarketRankings(true, {
        latitude: normalizedLatitude,
        longitude: normalizedLongitude,
        radiusKm: discoveryRadiusKm,
        requestToken,
      });
      const datasetForDistance = refreshedMarkets;
      const nearestMarket = datasetForDistance
        .map((market) => ({
          ...market,
          resolvedDistanceKm: resolveMarketDistance(market, { latitude: normalizedLatitude, longitude: normalizedLongitude }),
        }))
        .filter((market) => market.resolvedDistanceKm !== null)
        .sort((left, right) => left.resolvedDistanceKm - right.resolvedDistanceKm)[0];
      if (resolveAddress) {
        await resolveAddressFromCoordinates({
          latitude: normalizedLatitude,
          longitude: normalizedLongitude,
          fallbackLabel: addressLabel,
          silent: true,
          requestToken,
        });
      }
      if (notificationPermission === "granted" && nearestMarket && notifyNearestMarket) {
        sendActivationNotification(
          "Mercados atualizados",
          `${nearestMarket.market} esta a ${formatDistance(nearestMarket.resolvedDistanceKm)} de voce.`
        );
      }
      if (!silent) {
        setStatus(
          `${successMessage} Mercados salvos carregados. Novos locais reais continuam sincronizando em segundo plano.`,
          "success"
        );
      }
      return refreshedMarkets;
    } catch (error) {
      setStatus(getApiErrorMessage(error, "Erro ao sincronizar localizacao."), "error");
      return [];
    }
  }, [
    activeAuthHeaders,
    configuredDiscoveryRadiusKm,
    invalidateExpiredSession,
    loadMarketRankings,
    nextLocationRequestToken,
    notificationPermission,
    refreshMarketDataset,
    resolveAddressFromCoordinates,
    sendActivationNotification,
    setStatus,
    syncDisplayedAddress,
  ]);

  const requestCurrentLocation = useCallback(({
    persistProfile = true,
    silent = false,
    announceFailure = true,
    source = "manual",
  } = {}) => {
    const isAutomaticSource = source === "auto" || source === "startup";
    if (isAutomaticSource && manualLocationOverrideRef.current) {
      return;
    }
    if (!isAutomaticSource) {
      manualLocationOverrideRef.current = false;
    }
    if (!navigator.geolocation) {
      setLocationPermission("unsupported");
      if (!silent) {
        setStatus("Seu dispositivo nao suporta geolocalizacao no navegador.", "error");
      }
      return;
    }
    if (!secureContextAvailable) {
      if (!silent) {
        setStatus("O navegador pode bloquear GPS em HTTP. Para liberar no celular, use HTTPS ou localhost.", "warn");
      }
      return;
    }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        await syncUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          persistProfile,
          silent,
          successMessage: "Localizacao do dispositivo sincronizada com o mapa.",
          source,
        });
        setIsLocating(false);
      },
      (error) => {
        setIsLocating(false);
        if (error.code === 1) {
          setLocationPermission("denied");
        }
        if (announceFailure && !silent) {
          setStatus("Nao foi possivel usar a localizacao do dispositivo. Verifique as permissoes.", "error");
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  }, [secureContextAvailable, setStatus, syncUserLocation]);

  const selectAddressSuggestion = useCallback(async (suggestion, { persistProfile = true } = {}) => {
    if (!suggestion) {
      return false;
    }
    if (isApproximateAddressSuggestion(suggestion) && looksLikeSpecificAddressQuery(addressQuery)) {
      setAddressSuggestions((current) => (current.length > 0 ? current : [suggestion]));
      setStatus(
        "Esse resultado e aproximado e cairia no centro da cidade. Digite rua + numero + cidade/estado ou escolha uma sugestao mais precisa.",
        "warn"
      );
      return false;
    }
    manualLocationOverrideRef.current = true;
    hasSyncedDeviceLocationRef.current = true;
    syncDisplayedAddress(suggestion.label || suggestion.address || "");
    try {
      await syncUserLocation({
        latitude: suggestion.latitude,
        longitude: suggestion.longitude,
        persistProfile,
        silent: false,
        successMessage: persistProfile
          ? "Endereco aplicado e salvo com sucesso."
          : "Endereco aplicado com sucesso.",
        addressLabel: suggestion.label || suggestion.address || "",
        markPermissionGranted: false,
        notifyNearestMarket: false,
        resolveAddress: false,
      });
      return true;
    } catch (error) {
      setStatus(getApiErrorMessage(error, "Erro ao aplicar endereco salvo."), "error");
      return false;
    }
  }, [addressQuery, setStatus, syncDisplayedAddress, syncUserLocation]);

  const syncPushDeviceToken = useCallback(async (tokenValue) => {
    const normalizedToken = String(tokenValue || "").trim();
    if (!normalizedToken || !hasAuthHeaders(activeAuthHeaders)) {
      return null;
    }
    registeredPushTokenRef.current = normalizedToken;
    try {
      const response = await api.post(
        "/users/me/device-token",
        {
          platform: getNativePushPlatform(),
          provider: "fcm",
          token: normalizedToken,
          device_label: nativePushRuntime
            ? `${getNativePushPlatform()} mobile`
            : "web browser",
          app_version: process.env.REACT_APP_APP_VERSION || "1.2.0",
          locale:
            (typeof navigator !== "undefined" ? navigator.language : "") ||
            language,
          push_enabled: true,
        },
        { headers: activeAuthHeaders }
      );
      await loadDeviceTokens();
      return response.data || null;
    } catch (error) {
      if (isAuthSessionError(error)) {
        invalidateExpiredSession();
        return null;
      }
      setStatus(getApiErrorMessage(error, "Erro ao sincronizar notificacoes do dispositivo."), "error");
      return null;
    }
  }, [activeAuthHeaders, invalidateExpiredSession, language, loadDeviceTokens, nativePushRuntime, setStatus]);

  const requestNotificationAccess = useCallback(async () => {
    if (!supportsNotifications) {
      setNotificationPermission("unsupported");
      setStatus("Seu navegador nao suporta notificacoes.", "warn");
      return;
    }
    setIsRequestingNotifications(true);
    try {
      const nextPermission = await requestPushPermissionAccess();
      setNotificationPermission(nextPermission);
      if (nextPermission === "granted") {
        sendActivationNotification(
          translateText("PennyPantry pronto"),
          translateText("Voce vai receber avisos quando os mercados forem atualizados.")
        );
        setStatus("Notificacoes habilitadas com sucesso.", "success");
      } else {
        setStatus("Notificacoes nao foram liberadas.", "warn");
      }
    } finally {
      setIsRequestingNotifications(false);
    }
  }, [sendActivationNotification, setStatus, supportsNotifications, translateText]);

  const deleteAccount = useCallback(async () => {
    if (!hasAuthHeaders(activeAuthHeaders)) {
      setStatus("Voce precisa entrar na conta antes de excluir o cadastro.", "error");
      return;
    }
    if (!deleteAccountForm.current_password.trim()) {
      setStatus("Informe sua senha atual para excluir a conta.", "error");
      return;
    }
    if (deleteAccountForm.confirmation_text.trim().toUpperCase() !== "DELETE") {
      setStatus('Digite "DELETE" para confirmar a exclusao da conta.', "error");
      return;
    }

    setIsDeletingAccount(true);
    try {
      await api.post(
        "/users/me/delete-account",
        {
          current_password: deleteAccountForm.current_password,
          confirmation_text: deleteAccountForm.confirmation_text,
        },
        { headers: activeAuthHeaders }
      );
      registeredPushTokenRef.current = "";
      localStorage.removeItem("token");
      if (typeof onLogout === "function") {
        onLogout();
      } else {
        window.location.reload();
      }
    } catch (error) {
      setStatus(getApiErrorMessage(error, "Erro ao excluir a conta."), "error");
    } finally {
      setIsDeletingAccount(false);
    }
  }, [activeAuthHeaders, deleteAccountForm, onLogout, setStatus]);

  const saveUserSettings = useCallback(async () => {
    if (!hasAuthHeaders(activeAuthHeaders)) {
      setStatus("Voce precisa estar logado para salvar configuracoes.", "error");
      return;
    }
    const radiusKm = parseDistanceInputToKm(profileForm.discovery_radius_miles, { language });
    if (!Number.isFinite(radiusKm) || radiusKm <= 0) {
      setStatus(`Informe um raio de descoberta valido em ${distanceUnitLabel}.`, "error");
      return;
    }
    setIsSavingSettings(true);
    try {
      const payload = {
        full_name: profileForm.full_name,
        preferred_language: profileForm.preferred_language || language,
        default_search_radius_km: radiusKm,
      };
      if (userLocation) {
        payload.default_latitude = userLocation.latitude;
        payload.default_longitude = userLocation.longitude;
      } else if (hasValidCoordinates(userProfile?.default_latitude, userProfile?.default_longitude)) {
        payload.default_latitude = userProfile.default_latitude;
        payload.default_longitude = userProfile.default_longitude;
      }
      const response = await api.patch("/users/me", payload, { headers: activeAuthHeaders });
      setUserProfile(response.data);
      if (response.data?.preferred_language && response.data.preferred_language !== language) {
        setLanguage(response.data.preferred_language);
      }
      setProfileForm({
        full_name: response.data.full_name || "",
        preferred_language: response.data.preferred_language || language,
        discovery_radius_miles: formatDistanceInputValue(
          response.data.default_search_radius_km || LOCATION_DISCOVERY_RADIUS_KM,
          { language: response.data.preferred_language || language }
        ),
      });
      if (hasValidCoordinates(response.data.default_latitude, response.data.default_longitude)) {
        lastMapAutoRefreshKeyRef.current = "";
        setSearchLat(formatCoordinate(response.data.default_latitude));
        setSearchLng(formatCoordinate(response.data.default_longitude));
        setSelectedMarketId(null);
        setSelectedMarketSnapshot(null);
        setSelectedMapPoint(null);
        await resolveAddressFromCoordinates({
          latitude: response.data.default_latitude,
          longitude: response.data.default_longitude,
          fallbackLabel: addressQuery || selectedAddressLabel,
          silent: true,
        });
        await refreshMarketDataset({
          discoveryLatitude: response.data.default_latitude,
          discoveryLongitude: response.data.default_longitude,
          radiusKm,
          referenceLatitude: response.data.default_latitude,
          referenceLongitude: response.data.default_longitude,
          silent: true,
          autoDiscover: true,
        });
        await loadMarketRankings(true, {
          latitude: response.data.default_latitude,
          longitude: response.data.default_longitude,
          radiusKm,
        });
      } else if (userLocation) {
        lastMapAutoRefreshKeyRef.current = "";
        setSelectedMarketId(null);
        setSelectedMarketSnapshot(null);
        setSelectedMapPoint(null);
        await refreshMarketDataset({
          discoveryLatitude: userLocation.latitude,
          discoveryLongitude: userLocation.longitude,
          radiusKm,
          referenceLatitude: userLocation.latitude,
          referenceLongitude: userLocation.longitude,
          silent: true,
          autoDiscover: true,
        });
        await loadMarketRankings(true, {
          latitude: userLocation.latitude,
          longitude: userLocation.longitude,
          radiusKm,
        });
      }
      setStatus("Configuracoes da conta salvas. Raio de descoberta atualizado.", "success");
    } catch (error) {
      setStatus(getApiErrorMessage(error, "Erro ao salvar configuracoes."), "error");
    } finally {
      setIsSavingSettings(false);
    }
  }, [
    addressQuery,
    activeAuthHeaders,
    distanceUnitLabel,
    language,
    loadMarketRankings,
    profileForm,
    refreshMarketDataset,
    resolveAddressFromCoordinates,
    selectedAddressLabel,
    setLanguage,
    setStatus,
    userLocation,
    userProfile,
  ]);

  const saveCurrentAddress = useCallback(async () => {
    if (!hasAuthHeaders(activeAuthHeaders)) {
      setStatus("Entre na conta para salvar enderecos.", "warn");
      return;
    }
    if (!userLocation) {
      setStatus("Escolha um endereco ou use a localizacao antes de salvar.", "warn");
      return;
    }
    try {
      await api.post(
        "/users/me/addresses",
        {
          label: newAddressLabel || "Endereco",
          address: selectedAddressLabel || addressQuery || "Endereco atual",
          latitude: userLocation.latitude,
          longitude: userLocation.longitude,
          is_default: false,
        },
        { headers: activeAuthHeaders }
      );
      await loadSavedAddresses();
      setStatus("Endereco salvo com sucesso.", "success");
    } catch (error) {
      setStatus(getApiErrorMessage(error, "Erro ao salvar endereco."), "error");
    }
  }, [
    addressQuery,
    activeAuthHeaders,
    loadSavedAddresses,
    newAddressLabel,
    selectedAddressLabel,
    setStatus,
    userLocation,
  ]);

  const setDefaultAddress = useCallback(async (addressId) => {
    if (!hasAuthHeaders(activeAuthHeaders)) {
      return;
    }
    try {
      const response = await api.post(`/users/me/addresses/${addressId}/default`, null, {
        headers: activeAuthHeaders,
      });
      await loadSavedAddresses();
      await selectAddressSuggestion(
        {
          label: response.data.address,
          address: response.data.address,
          latitude: response.data.latitude,
          longitude: response.data.longitude,
        },
        { persistProfile: true }
      );
    } catch (error) {
      setStatus(getApiErrorMessage(error, "Erro ao trocar endereco padrao."), "error");
    }
  }, [activeAuthHeaders, loadSavedAddresses, selectAddressSuggestion, setStatus]);

  const deleteSavedAddress = useCallback(async (addressId) => {
    if (!hasAuthHeaders(activeAuthHeaders)) {
      return;
    }
    try {
      await api.delete(`/users/me/addresses/${addressId}`, { headers: activeAuthHeaders });
      await loadSavedAddresses();
    } catch (error) {
      setStatus(getApiErrorMessage(error, "Erro ao remover endereco."), "error");
    }
  }, [activeAuthHeaders, loadSavedAddresses, setStatus]);

  const createPriceAlert = useCallback(async () => {
    if (!hasAuthHeaders(activeAuthHeaders)) {
      setStatus("Entre na conta para criar alertas.", "warn");
      return;
    }
    if (!selectedProductInsight?.product_id) {
      setStatus("Abra uma pagina de produto antes de criar alerta.", "warn");
      return;
    }
    const targetPrice = Number(String(alertTargetPrice).replace(",", "."));
    if (!Number.isFinite(targetPrice) || targetPrice <= 0) {
      setStatus("Informe um valor-alvo valido para o alerta.", "error");
      return;
    }
    const parsedMaxDistanceKm = String(alertMaxDistanceKm || "").trim()
      ? parseDistanceInputToKm(alertMaxDistanceKm, { language })
      : null;
    if (
      parsedMaxDistanceKm !== null &&
      (!Number.isFinite(parsedMaxDistanceKm) || parsedMaxDistanceKm <= 0)
    ) {
      setStatus(`Informe um raio maximo valido em ${distanceUnitLabel} ou deixe em branco.`, "error");
      return;
    }
    if (
      parsedMaxDistanceKm !== null &&
      !userLocation &&
      !hasValidCoordinates(userProfile?.default_latitude, userProfile?.default_longitude)
    ) {
      setStatus("Ative ou salve sua localizacao antes de criar um alerta por distancia.", "warn");
      return;
    }
    try {
      await api.post(
        "/users/me/alerts",
        {
          product_id: selectedProductInsight.product_id,
          target_price: targetPrice,
          max_distance_km: parsedMaxDistanceKm,
        },
        { headers: activeAuthHeaders }
      );
      setAlertTargetPrice("");
      await loadAlerts();
      setStatus(
        parsedMaxDistanceKm !== null
          ? `Alerta criado para ${selectedProductInsight.product} ate ${formatDistance(parsedMaxDistanceKm, { language })}.`
          : "Alerta de queda de preco criado.",
        "success"
      );
    } catch (error) {
      setStatus(getApiErrorMessage(error, "Erro ao criar alerta."), "error");
    }
  }, [
    alertMaxDistanceKm,
    alertTargetPrice,
    activeAuthHeaders,
    distanceUnitLabel,
    language,
    loadAlerts,
    selectedProductInsight,
    setStatus,
    userLocation,
    userProfile,
  ]);

  const removeAlert = useCallback(async (alertId) => {
    if (!hasAuthHeaders(activeAuthHeaders)) {
      return;
    }
    try {
      await api.delete(`/users/me/alerts/${alertId}`, { headers: activeAuthHeaders });
      await loadAlerts();
    } catch (error) {
      setStatus(getApiErrorMessage(error, "Erro ao remover alerta."), "error");
    }
  }, [activeAuthHeaders, loadAlerts, setStatus]);

  const markNotificationRead = useCallback(async (notificationId) => {
    if (!hasAuthHeaders(activeAuthHeaders)) {
      return;
    }
    try {
      await api.patch(`/users/me/notifications/${notificationId}/read`, null, {
        headers: activeAuthHeaders,
      });
      await loadNotifications({ silent: true });
    } catch (error) {
      setStatus(getApiErrorMessage(error, "Erro ao atualizar notificacao."), "error");
    }
  }, [activeAuthHeaders, loadNotifications, setStatus]);

  const buildSearchSuggestions = useCallback((responseData) => {
    const marketSuggestions = (responseData?.markets || []).slice(0, 4).map((market) => ({
      key: `market-${market.market_id}`,
      kind: "market",
      query: market.market || "",
      title: market.market || "",
      subtitle: [market.category, market.address].filter(Boolean).join(" | "),
      market,
    }));
    const productSuggestions = (responseData?.products || []).slice(0, 4).map((product) => ({
      key: `product-${product.product_id}`,
      kind: "product",
      query: product.product || "",
      title: product.product || "",
      subtitle: [
        product.brand,
        product.category,
        product.market
          ? t("search.suggestion.best_at", "Melhor em {{market}}", {
              market: product.market,
            })
          : "",
      ]
        .filter(Boolean)
        .join(" | "),
      product,
    }));
    return [...marketSuggestions, ...productSuggestions].slice(0, 6);
  }, [t]);

  const buildUnifiedSearchCacheKey = useCallback(
    (query, limit = 12) =>
      [
        normalizeTextKey(query),
        hasAuthHeaders(activeAuthHeaders) ? "auth" : "anon",
        Number(userLocation?.latitude || 0).toFixed(3),
        Number(userLocation?.longitude || 0).toFixed(3),
        limit,
      ].join(":"),
    [activeAuthHeaders, userLocation]
  );

  const handleSearchQueryChange = useCallback((value) => {
    setSearchProduct(value);
    if (normalizeTextKey(value) !== normalizeTextKey(selectedSearchSuggestionLabel)) {
      setSelectedSearchSuggestionLabel("");
    }
  }, [selectedSearchSuggestionLabel]);

  const openProductInsight = useCallback(async (productId) => {
    if (!productId) {
      return;
    }
    setIsSearchingProducts(true);
    try {
      const response = await api.get(`/products/${productId}/insights`, {
        params: {
          user_latitude: userLocation?.latitude,
          user_longitude: userLocation?.longitude,
        },
        headers: activeAuthHeaders || undefined,
      });
      setSelectedProductInsight(response.data);
      setStatus(
        t("search.status.product_opened", "Pagina do produto {{product}} aberta.", {
          product: response.data.product,
        }),
        "success"
      );
    } catch (error) {
      setStatus(getApiErrorMessage(error, "Erro ao abrir o produto."), "error");
    } finally {
      setIsSearchingProducts(false);
    }
  }, [activeAuthHeaders, setStatus, t, userLocation]);

  const fetchProductInsights = useCallback(async ({
    queryOverride = null,
    silent = false,
    openFirstProduct = true,
  } = {}) => {
    const product = String(queryOverride ?? searchProduct).trim();
    if (!product) {
      if (!silent) {
        setStatus(t("search.status.type_product", "Digite um produto para comparar."), "error");
      }
      return null;
    }
    setIsSearchingProducts(true);
    try {
      const cacheKey = buildUnifiedSearchCacheKey(product, 12);
      let responseData = searchResultCacheRef.current.get(cacheKey) || null;
      if (!responseData) {
        const response = await api.get("/search/unified", {
          params: {
            q: product,
            user_latitude: userLocation?.latitude,
            user_longitude: userLocation?.longitude,
          },
          headers: activeAuthHeaders || undefined,
        });
        responseData = response.data;
        searchResultCacheRef.current.set(cacheKey, responseData);
      }
      setUnifiedSearchResult(responseData);
      setSelectedCategoryFilter("");
      setSelectedBrandFilter("");
      if (openFirstProduct && responseData?.products?.[0]?.product_id) {
        await openProductInsight(responseData.products[0].product_id);
      } else if (!openFirstProduct) {
        setSelectedProductInsight(null);
      } else {
        setSelectedProductInsight(null);
      }
      if (!silent) {
        setStatus(
          t("search.status.results_found", "{{count}} resultados encontrados para {{product}}.", {
            count: (responseData?.markets?.length || 0) + (responseData?.products?.length || 0),
            product,
          }),
          "success"
        );
      }
      return responseData;
    } catch (error) {
      setUnifiedSearchResult(null);
      setSelectedProductInsight(null);
      if (!silent) {
        setStatus(getApiErrorMessage(error, "Erro ao buscar o produto."), "error");
      }
      return null;
    } finally {
      setIsSearchingProducts(false);
    }
  }, [activeAuthHeaders, buildUnifiedSearchCacheKey, openProductInsight, searchProduct, setStatus, t, userLocation]);

  const loadSelectedMarketPrices = useCallback(async (marketId) => {
    if (!marketId) {
      setSelectedMarketPrices([]);
      setSelectedMarketProductName("");
      setMarketCatalogSyncMeta({
        status: "idle",
        provider: "",
        imported: 0,
        skipped: 0,
        message: "",
      });
      return;
    }
    setIsLoadingMarketPrices(true);
    try {
      const response = await api.get(`/markets/${marketId}/prices`, {
        params: { auto_sync: true },
      });
      const syncStatus = String(
        response.headers["x-pennypantry-catalog-sync-status"] ||
          response.headers["x-priceradar-catalog-sync-status"] ||
          "idle"
      );
      const syncProvider = String(
        response.headers["x-pennypantry-catalog-sync-provider"] ||
          response.headers["x-priceradar-catalog-sync-provider"] ||
          ""
      );
      const syncImported = Number(
        response.headers["x-pennypantry-catalog-sync-imported"] ||
          response.headers["x-priceradar-catalog-sync-imported"] ||
          0
      );
      const syncSkipped = Number(
        response.headers["x-pennypantry-catalog-sync-skipped"] ||
          response.headers["x-priceradar-catalog-sync-skipped"] ||
          0
      );
      const syncMessage = String(
        response.headers["x-pennypantry-catalog-sync-message"] ||
          response.headers["x-priceradar-catalog-sync-message"] ||
          ""
      );
      const syncMeta = {
        status: syncStatus,
        provider: syncProvider,
        imported: Number.isFinite(syncImported) ? syncImported : 0,
        skipped: Number.isFinite(syncSkipped) ? syncSkipped : 0,
        message: syncMessage,
      };
      setMarketCatalogSyncMeta(syncMeta);
      setSelectedMarketPrices(response.data || []);
      if (syncMeta.status === "synced") {
        setStatus(
          syncMeta.imported > 0
            ? `Catalogo sincronizado automaticamente com ${syncMeta.imported} preco(s) novo(s).`
            : "Catalogo sincronizado automaticamente.",
          "success"
        );
      } else if (syncMeta.status === "error") {
        setStatus(syncMeta.message || "Erro ao sincronizar o catalogo automatico.", "warn");
      } else if (
        (syncMeta.status === "no_provider" || syncMeta.status === "no_data") &&
        (!response.data || response.data.length === 0)
      ) {
        setStatus(
          "Este mercado ainda nao tem fonte automatica de produtos e precos. Exibindo apenas o catalogo mais recente disponivel.",
          "info"
        );
      }
    } catch (error) {
      setSelectedMarketPrices([]);
      setSelectedMarketProductName("");
      setMarketCatalogSyncMeta({
        status: "error",
        provider: "",
        imported: 0,
        skipped: 0,
        message: getApiErrorMessage(error, "Erro ao carregar produtos do mercado."),
      });
      setStatus(getApiErrorMessage(error, "Erro ao carregar produtos do mercado."), "error");
    } finally {
      setIsLoadingMarketPrices(false);
    }
  }, [setStatus]);

  const applyMarketSelection = useCallback((market, statusMessage, options = {}) => {
    if (!market) {
      return;
    }
    const { goToMap = false, activeTabId = null } = options;
    setSelectedMarketId(market.market_id);
    setSelectedMarketSnapshot(market);
    setSelectedMapPoint(
      hasValidCoordinates(market.latitude, market.longitude)
        ? { latitude: market.latitude, longitude: market.longitude }
        : null
    );
    if (activeTabId) {
      setActiveTab(activeTabId);
    } else if (goToMap) {
      setActiveTab("map");
    }
    if (statusMessage) {
      setStatus(statusMessage, "success");
    }
  }, [setStatus]);

  const previewMarketSelection = useCallback((market, statusMessage) => {
    applyMarketSelection(market, statusMessage, {
      goToMap: false,
    });
  }, [applyMarketSelection]);

  const openMarketPage = useCallback((market = null, options = {}) => {
    const targetMarket = market || selectedMarket;
    if (!targetMarket) {
      setStatus("Selecione um mercado no mapa ou na busca antes de abrir a pagina dele.", "warn");
      return;
    }
    applyMarketSelection(targetMarket, options.statusMessage, {
      activeTabId: "market",
    });
  }, [applyMarketSelection, selectedMarket, setStatus]);

  const selectSearchSuggestion = useCallback(async (suggestion) => {
    if (!suggestion) {
      return;
    }
    setSearchProduct(suggestion.query || "");
    setSelectedSearchSuggestionLabel(suggestion.query || "");
    setSearchSuggestions([]);
    setActiveTab("search");

    if (suggestion.kind === "market" && suggestion.market) {
      await fetchProductInsights({
        queryOverride: suggestion.query,
        silent: true,
        openFirstProduct: false,
      });
      openMarketPage(suggestion.market, {
        statusMessage:
          t?.(
            "status.market.open.search_suggestion",
            "{{market}} aberto pelas sugestoes da busca.",
            {
              market: suggestion.market.market,
            }
          ) || `${suggestion.market.market} aberto pelas sugestoes da busca.`,
      });
      return;
    }

    if (suggestion.kind === "product" && suggestion.product?.product_id) {
      await fetchProductInsights({
        queryOverride: suggestion.query,
        silent: true,
        openFirstProduct: false,
      });
      await openProductInsight(suggestion.product.product_id);
    }
  }, [fetchProductInsights, openMarketPage, openProductInsight, t]);

  const clearSelectedMarket = useCallback(() => {
    setSelectedMarketId(null);
    setSelectedMarketSnapshot(null);
    setSelectedMapPoint(null);
    setSelectedMarketPrices([]);
    setSelectedMarketProductName("");
    setMarketCatalogSyncMeta({
      status: "idle",
      provider: "",
      imported: 0,
      skipped: 0,
      message: "",
    });
    setStatus("Selecao de mercado limpa. Escolha outro marcador no mapa.", "info");
  }, [setStatus]);

  const handleMapPick = useCallback((point) => {
    setSelectedMarketId(null);
    setSelectedMarketSnapshot(null);
    setSelectedMapPoint(point);
    setSelectedMarketPrices([]);
    setSelectedMarketProductName("");
    setMarketCatalogSyncMeta({
      status: "idle",
      provider: "",
      imported: 0,
      skipped: 0,
      message: "",
    });
    setActiveTab("map");
    setStatus("Ponto manual marcado. Para este fluxo novo, priorize tocar em um mercado real e abrir a pagina dele.", "info");
  }, [setStatus]);

  const handleViewportChange = useCallback(({ latitude, longitude, radiusKm }) => {
    if (!hasValidCoordinates(latitude, longitude)) {
      return;
    }
    if (userLocation) {
      return;
    }
    const roundedSignature = [
      Number(latitude).toFixed(3),
      Number(longitude).toFixed(3),
      Number(radiusKm || MAP_DISCOVERY_RADIUS_KM_FALLBACK).toFixed(1),
    ].join(":");
    if (roundedSignature === lastViewportSignatureRef.current) {
      return;
    }
    if (viewportDiscoveryTimeoutRef.current) {
      window.clearTimeout(viewportDiscoveryTimeoutRef.current);
    }
    viewportDiscoveryTimeoutRef.current = window.setTimeout(async () => {
      lastViewportSignatureRef.current = roundedSignature;
      const referenceLatitude = userLocation?.latitude ?? latitude;
      const referenceLongitude = userLocation?.longitude ?? longitude;
      await refreshMarketDataset({
        discoveryLatitude: latitude,
        discoveryLongitude: longitude,
        radiusKm: radiusKm || MAP_DISCOVERY_RADIUS_KM_FALLBACK,
        referenceLatitude,
        referenceLongitude,
        silent: true,
      });
    }, 500);
  }, [refreshMarketDataset, userLocation]);

  const buildShoppingPayload = useCallback(() => {
    const products = extractProductsFromText(shoppingListText);
    if (products.length === 0) {
      throw new Error("Adicione ao menos um item na lista de compras.");
    }
    return {
      name: "Lista mobile",
      products,
      user_latitude: userLocation?.latitude,
      user_longitude: userLocation?.longitude,
      max_distance_km: configuredDiscoveryRadiusKm,
    };
  }, [configuredDiscoveryRadiusKm, shoppingListText, userLocation]);

  const calculateShoppingList = useCallback(async () => {
    try {
      const response = await api.post(
        "/shopping-list",
        buildShoppingPayload(),
        buildOptionalAuthConfig(activeAuthHeaders)
      );
      setShoppingResult(response.data);
      setCompareResult(null);
      setRouteResult(null);
      setStatus(t("list.status.calculated", "Lista de compras calculada com sucesso."), "success");
    } catch (error) {
      const fallback = error instanceof Error ? error.message : "Erro ao calcular lista de compras.";
      setStatus(getApiErrorMessage(error, fallback), "error");
    }
  }, [activeAuthHeaders, buildShoppingPayload, setStatus, t]);

  const compareShoppingList = useCallback(async () => {
    try {
      const response = await api.post(
        "/compare",
        buildShoppingPayload(),
        buildOptionalAuthConfig(activeAuthHeaders)
      );
      setCompareResult(response.data);
      setShoppingResult((current) => ({
        ...(current || {}),
        requested_products: response.data?.requested_products || [],
        cheapest_market: response.data?.cheapest_market || null,
        best_market: response.data?.cheapest_market || null,
        nearest_market: response.data?.nearest_market || null,
        best_balance_market: response.data?.best_balance_market || null,
        best_value_market: response.data?.best_balance_market || null,
        evaluated_markets: response.data?.evaluated_markets || [],
        missing_products: response.data?.missing_products || [],
        shopping_list_id: current?.shopping_list_id || null,
        split_recommendations: current?.split_recommendations || [],
      }));
      setStatus(t("list.status.compared", "Comparativo de mercados atualizado."), "success");
    } catch (error) {
      const fallback = error instanceof Error ? error.message : "Erro ao comparar mercados.";
      setStatus(getApiErrorMessage(error, fallback), "error");
    }
  }, [activeAuthHeaders, buildShoppingPayload, setStatus, t]);

  const calculateBestRoute = useCallback(async () => {
    try {
      const response = await api.post(
        "/best-route",
        buildShoppingPayload(),
        buildOptionalAuthConfig(activeAuthHeaders)
      );
      setRouteResult(response.data);
      setStatus(t("list.status.route", "Rota economica calculada."), "success");
    } catch (error) {
      const fallback = error instanceof Error ? error.message : "Erro ao calcular melhor rota.";
      setStatus(getApiErrorMessage(error, fallback), "error");
    }
  }, [activeAuthHeaders, buildShoppingPayload, setStatus, t]);

  const createShoppingListAlert = useCallback(async () => {
    if (!hasAuthHeaders(activeAuthHeaders)) {
      setStatus("Entre na conta para criar alertas da lista.", "warn");
      return;
    }
    if (!shoppingResult?.shopping_list_id) {
      setStatus("Calcule e salve a lista antes de criar um alerta.", "warn");
      return;
    }
    const targetTotal = Number(String(shoppingListAlertTargetTotal).replace(",", "."));
    if (!Number.isFinite(targetTotal) || targetTotal <= 0) {
      setStatus("Informe um valor-alvo total valido para a lista.", "error");
      return;
    }
    const parsedMaxDistanceKm = String(shoppingListAlertMaxDistanceKm || "").trim()
      ? parseDistanceInputToKm(shoppingListAlertMaxDistanceKm, { language })
      : null;
    if (
      parsedMaxDistanceKm !== null &&
      (!Number.isFinite(parsedMaxDistanceKm) || parsedMaxDistanceKm <= 0)
    ) {
      setStatus(`Informe um raio maximo valido em ${distanceUnitLabel} ou deixe em branco.`, "error");
      return;
    }
    if (
      parsedMaxDistanceKm !== null &&
      !userLocation &&
      !hasValidCoordinates(userProfile?.default_latitude, userProfile?.default_longitude)
    ) {
      setStatus("Ative ou salve sua localizacao antes de criar um alerta por distancia.", "warn");
      return;
    }
    try {
      await api.post(
        "/users/me/shopping-list-alerts",
        {
          shopping_list_id: shoppingResult.shopping_list_id,
          target_total_price: targetTotal,
          max_distance_km: parsedMaxDistanceKm,
        },
        { headers: activeAuthHeaders }
      );
      setShoppingListAlertTargetTotal("");
      await loadShoppingListAlerts();
      await loadNotifications({ silent: true });
      setStatus("Alerta da lista criado com sucesso.", "success");
    } catch (error) {
      setStatus(getApiErrorMessage(error, "Erro ao criar alerta da lista."), "error");
    }
  }, [
    activeAuthHeaders,
    distanceUnitLabel,
    language,
    loadNotifications,
    loadShoppingListAlerts,
    setStatus,
    shoppingListAlertMaxDistanceKm,
    shoppingListAlertTargetTotal,
    shoppingResult,
    userLocation,
    userProfile,
  ]);

  const removeShoppingListAlert = useCallback(async (alertId) => {
    if (!hasAuthHeaders(activeAuthHeaders)) {
      return;
    }
    try {
      await api.delete(`/users/me/shopping-list-alerts/${alertId}`, { headers: activeAuthHeaders });
      await loadShoppingListAlerts();
    } catch (error) {
      setStatus(getApiErrorMessage(error, "Erro ao remover alerta da lista."), "error");
    }
  }, [activeAuthHeaders, loadShoppingListAlerts, setStatus]);

  const loadMarketAroundUser = useCallback(() => {
    if (!userLocation) {
      setStatus(
        `Ative a localizacao para sincronizar os mercados e lojas em um raio de ${configuredDiscoveryRadiusKmLabel}.`,
        "warn"
      );
      return Promise.resolve([]);
    }
    return syncUserLocation({
      latitude: userLocation.latitude,
      longitude: userLocation.longitude,
      persistProfile: true,
      successMessage: "Mapa sincronizado com os locais reais ao redor da sua posicao.",
    });
  }, [configuredDiscoveryRadiusKmLabel, setStatus, syncUserLocation, userLocation]);

  useEffect(() => {
    if (!selectedMarketId) {
      setSelectedMarketPrices([]);
      setSelectedMarketProductName("");
      setMarketCatalogSyncMeta({
        status: "idle",
        provider: "",
        imported: 0,
        skipped: 0,
        message: "",
      });
      return;
    }
    loadSelectedMarketPrices(selectedMarketId);
  }, [loadSelectedMarketPrices, selectedMarketId]);

  useEffect(() => {
    if (selectedMarketPrices.length === 0) {
      if (selectedMarketProductName) {
        setSelectedMarketProductName("");
      }
      return;
    }

    const hasSelectedProduct = selectedMarketPrices.some(
      (entry) => normalizeTextKey(entry.product) === normalizeTextKey(selectedMarketProductName)
    );

    if (!hasSelectedProduct) {
      setSelectedMarketProductName(selectedMarketPrices[0].product || "");
    }
  }, [selectedMarketPrices, selectedMarketProductName]);

  useEffect(() => {
    if (!searchProduct.trim()) {
      setUnifiedSearchResult(null);
      setSelectedProductInsight(null);
      setSelectedCategoryFilter("");
      setSelectedBrandFilter("");
      setSearchSuggestions([]);
      setSelectedSearchSuggestionLabel("");
    }
  }, [searchProduct]);

  useEffect(() => {
    const trimmedQuery = searchProduct.trim();
    const normalizedQuery = normalizeTextKey(trimmedQuery);
    const normalizedSelectedSuggestion = normalizeTextKey(selectedSearchSuggestionLabel);

    if (searchLookupTimeoutRef.current) {
      window.clearTimeout(searchLookupTimeoutRef.current);
      searchLookupTimeoutRef.current = null;
    }

    if (trimmedQuery.length < 2 || (normalizedSelectedSuggestion && normalizedQuery === normalizedSelectedSuggestion)) {
      setIsSearchingSuggestions(false);
      setSearchSuggestions([]);
      return;
    }

    searchLookupTimeoutRef.current = window.setTimeout(async () => {
      setIsSearchingSuggestions(true);
      try {
        const cacheKey = buildUnifiedSearchCacheKey(trimmedQuery, 6);
        let responseData = searchSuggestionCacheRef.current.get(cacheKey) || null;
        if (!responseData) {
          const response = await api.get("/search/unified", {
            params: {
              q: trimmedQuery,
              user_latitude: userLocation?.latitude,
              user_longitude: userLocation?.longitude,
              limit: 6,
            },
            headers: activeAuthHeaders || undefined,
          });
          responseData = response.data;
          searchSuggestionCacheRef.current.set(cacheKey, responseData);
        }
        setSearchSuggestions(buildSearchSuggestions(responseData));
      } catch (error) {
        setSearchSuggestions([]);
      } finally {
        setIsSearchingSuggestions(false);
      }
    }, 320);

    return () => {
      if (searchLookupTimeoutRef.current) {
        window.clearTimeout(searchLookupTimeoutRef.current);
        searchLookupTimeoutRef.current = null;
      }
    };
  }, [
    activeAuthHeaders,
    buildSearchSuggestions,
    buildUnifiedSearchCacheKey,
    searchProduct,
    selectedSearchSuggestionLabel,
    userLocation,
  ]);

  useEffect(() => {
    let isActive = true;

    async function bootstrapPushListeners() {
      const existingCleanup = pushListenerCleanupRef.current;
      if (existingCleanup) {
        await existingCleanup();
        pushListenerCleanupRef.current = null;
      }

      const cleanup = await attachPushListeners({
        onToken: async (tokenValue) => {
          registeredPushTokenRef.current = tokenValue;
          if (hasAuthHeaders(activeAuthHeaders)) {
            await syncPushDeviceToken(tokenValue);
          }
        },
        onNotificationReceived: async () => {
          if (hasAuthHeaders(activeAuthHeaders)) {
            await loadNotifications({ silent: true });
          }
        },
        onActionPerformed: async () => {
          if (hasAuthHeaders(activeAuthHeaders)) {
            await loadNotifications({ silent: true });
          }
        },
        onRegistrationError: () => {
          if (isActive) {
            setStatus("Nao foi possivel registrar notificacoes neste dispositivo.", "warn");
          }
        },
      });

      pushListenerCleanupRef.current = cleanup;

      if (nativePushRuntime) {
        const permissionState = await getPushPermissionState();
        if (isActive && permissionState === "granted") {
          await registerForPushNotifications();
        } else if (isActive && permissionState === "prompt" && hasAuthHeaders(activeAuthHeaders)) {
          const nextPermission = await registerPushNotifications();
          if (isActive) {
            setNotificationPermission(nextPermission);
          }
        }
      }
    }

    bootstrapPushListeners();

    return () => {
      isActive = false;
      const cleanup = pushListenerCleanupRef.current;
      pushListenerCleanupRef.current = null;
      if (cleanup) {
        cleanup();
      }
    };
  }, [activeAuthHeaders, loadNotifications, nativePushRuntime, setStatus, syncPushDeviceToken]);

  useEffect(() => {
    let isActive = true;
    async function bootstrapDashboard() {
      try {
        const bootstrapRequestToken = nextLocationRequestToken();
        await refreshPermissionStates();
        let profile = null;
        if (hasAuthHeaders(activeAuthHeaders)) {
          try {
            const profileResponse = await api.get("/users/me", { headers: activeAuthHeaders });
            profile = profileResponse.data;
            if (!isActive) {
              return;
            }
            if (profile?.preferred_language && profile.preferred_language !== language) {
              setLanguage(profile.preferred_language);
            }
            setUserProfile(profile);
            setProfileForm({
              full_name: profile.full_name || "",
              preferred_language: profile.preferred_language || language,
              discovery_radius_miles: formatDistanceInputValue(
                profile.default_search_radius_km || LOCATION_DISCOVERY_RADIUS_KM,
                { language: profile.preferred_language || language }
              ),
            });
            const subscription = await loadBillingOverview();
            if (!isActive) {
              return;
            }
            if (subscription && !subscription.access_granted) {
              setActiveTab("settings");
              return;
            }
            await loadFavorites();
            await loadAlerts();
            await loadShoppingListAlerts();
            await loadSavedAddresses();
            await loadNotifications({ silent: true });
            await loadDeviceTokens();
            if (profile.is_admin) {
              const dashboardResponse = await api.get("/admin/dashboard", { headers: activeAuthHeaders });
              const analyticsResponse = await api.get("/analytics/overview", { headers: activeAuthHeaders });
              const suspiciousResponse = await api.get("/admin/suspicious-prices", { headers: activeAuthHeaders });
              if (isActive) {
                setAdminDashboard(dashboardResponse.data || null);
                setAnalyticsOverview(analyticsResponse.data || null);
                setSuspiciousPrices(suspiciousResponse.data || []);
              }
            }
          } catch (error) {
            if (isAuthSessionError(error)) {
              invalidateExpiredSession();
              profile = null;
            } else {
              throw error;
            }
          }
        }
        if (hasValidCoordinates(profile?.default_latitude, profile?.default_longitude)) {
          setSearchLat(formatCoordinate(profile.default_latitude));
          setSearchLng(formatCoordinate(profile.default_longitude));
          await resolveAddressFromCoordinates({
            latitude: profile.default_latitude,
            longitude: profile.default_longitude,
            silent: true,
            requestToken: bootstrapRequestToken,
          });
          await refreshMarketDataset({
            discoveryLatitude: profile.default_latitude,
            discoveryLongitude: profile.default_longitude,
            radiusKm: profile.default_search_radius_km || LOCATION_DISCOVERY_RADIUS_KM,
            referenceLatitude: profile.default_latitude,
            referenceLongitude: profile.default_longitude,
            silent: true,
            requestToken: bootstrapRequestToken,
          });
        } else {
          await loadMarkets({
            silent: true,
            requestToken: bootstrapRequestToken,
          });
        }
        await loadMarketRankings(true, {
          latitude: profile?.default_latitude,
          longitude: profile?.default_longitude,
          radiusKm: profile?.default_search_radius_km || LOCATION_DISCOVERY_RADIUS_KM,
          requestToken: bootstrapRequestToken,
        });
        if (
          navigator.geolocation &&
          secureContextAvailable &&
          !didAutoLocateRef.current
        ) {
          didAutoLocateRef.current = true;
          requestCurrentLocation({
            persistProfile: true,
            silent: true,
            announceFailure: false,
            source: "startup",
          });
        }
      } catch (error) {
        if (isActive) {
          setStatus(getApiErrorMessage(error, "Erro ao carregar o app."), "error");
        }
      }
    }
    bootstrapDashboard();
    return () => {
      isActive = false;
    };
  }, [
    activeAuthHeaders,
    language,
    loadAlerts,
    loadBillingOverview,
    loadDeviceTokens,
    loadFavorites,
    loadNotifications,
    loadShoppingListAlerts,
    loadSavedAddresses,
    loadMarketRankings,
    loadMarkets,
    invalidateExpiredSession,
    nextLocationRequestToken,
    refreshMarketDataset,
    refreshPermissionStates,
    resolveAddressFromCoordinates,
    requestCurrentLocation,
    secureContextAvailable,
    setLanguage,
    setStatus,
  ]);

  useEffect(() => {
    if (
      locationPermission !== "granted" ||
      !navigator.geolocation ||
      !secureContextAvailable ||
      isLocating ||
      hasSyncedDeviceLocationRef.current ||
      didAutoLocateRef.current
    ) {
      return;
    }

    didAutoLocateRef.current = true;
    requestCurrentLocation({
      persistProfile: true,
      silent: true,
      announceFailure: false,
      source: "startup",
    });
  }, [isLocating, locationPermission, requestCurrentLocation, secureContextAvailable]);

  useEffect(() => {
    if (
      activeTab !== "map" ||
      !userLocation ||
      !secureContextAvailable ||
      isLoadingMarkets ||
      isDiscoveringRadiusMarkets
    ) {
      return;
    }

    const refreshKey = [
      userLocation.latitude.toFixed(5),
      userLocation.longitude.toFixed(5),
      Number(configuredDiscoveryRadiusKm || LOCATION_DISCOVERY_RADIUS_KM).toFixed(1),
    ].join(":");

    if (lastMapAutoRefreshKeyRef.current === refreshKey) {
      return;
    }

    lastMapAutoRefreshKeyRef.current = refreshKey;
    loadMarkets({
      latitude: userLocation.latitude,
      longitude: userLocation.longitude,
      radiusKm: configuredDiscoveryRadiusKm,
      silent: true,
      autoDiscover: true,
      limit: 50,
    });
  }, [
    activeTab,
    configuredDiscoveryRadiusKm,
    isDiscoveringRadiusMarkets,
    isLoadingMarkets,
    loadMarkets,
    secureContextAvailable,
    userLocation,
  ]);

  useEffect(() => {
    if (!hasAuthHeaders(activeAuthHeaders) || (billingOverview && !billingOverview.access_granted)) {
      return undefined;
    }
    loadNotifications({ silent: true });
    const interval = window.setInterval(() => {
      loadNotifications({ silent: true });
    }, 45000);
    return () => window.clearInterval(interval);
  }, [activeAuthHeaders, billingOverview, loadNotifications]);

  useEffect(() => {
    loadAdminData();
  }, [loadAdminData]);

  useEffect(() => {
    return () => {
      if (viewportDiscoveryTimeoutRef.current) {
        window.clearTimeout(viewportDiscoveryTimeoutRef.current);
      }
      if (searchLookupTimeoutRef.current) {
        window.clearTimeout(searchLookupTimeoutRef.current);
      }
    };
  }, []);

  const statusBanner = statusState.message ? (
    <div className={`status-banner ${statusState.tone}`} style={{ marginBottom: 18 }}>
      {statusState.message}
    </div>
  ) : null;
  const activeTabLabel = localizedTabs.find((tab) => tab.id === activeTab)?.label || "PennyPantry";
  const topbarAccountLabel =
    userProfile?.full_name || userProfile?.email || t("dashboard.hero.user", "User");

  const renderActivePage = () => {
    if (activeTab === "search") {
      return (
        <SearchPage
          alertMaxDistanceKm={alertMaxDistanceKm}
          alertTargetPrice={alertTargetPrice}
          createPriceAlert={createPriceAlert}
          configuredDiscoveryRadiusKmLabel={configuredDiscoveryRadiusKmLabel}
          currency={activeCurrency}
          distanceUnitLabel={distanceUnitLabel}
          isSearchingProducts={isSearchingProducts}
          isSearchingSuggestions={isSearchingSuggestions}
          language={language}
          openMarketPage={openMarketPage}
          openProductInsight={openProductInsight}
          searchQuery={searchProduct}
          searchSuggestions={searchSuggestions}
          selectSearchSuggestion={selectSearchSuggestion}
          selectedBrandFilter={selectedBrandFilter}
          selectedCategoryFilter={selectedCategoryFilter}
          selectedProductInsight={selectedProductInsight}
          setAlertMaxDistanceKm={setAlertMaxDistanceKm}
          setAlertTargetPrice={setAlertTargetPrice}
          setBrandFilter={setSelectedBrandFilter}
          setCategoryFilter={setSelectedCategoryFilter}
          setSearchQuery={handleSearchQueryChange}
          submitSearch={() => fetchProductInsights()}
          t={t}
          toggleFavorite={toggleFavorite}
          unifiedSearchResult={unifiedSearchResult}
        />
      );
    }
    if (activeTab === "list") {
      return (
        <ListPage
          authHeaders={activeAuthHeaders}
          calculateBestRoute={calculateBestRoute}
          calculateShoppingList={calculateShoppingList}
          compareResult={compareResult}
          compareShoppingList={compareShoppingList}
          createShoppingListAlert={createShoppingListAlert}
          configuredDiscoveryRadiusKmLabel={configuredDiscoveryRadiusKmLabel}
          currency={activeCurrency}
          distanceUnitLabel={distanceUnitLabel}
          marketCatalog={marketCatalog}
          openMarketPage={openMarketPage}
          removeShoppingListAlert={removeShoppingListAlert}
          routeResult={routeResult}
          setShoppingListAlertMaxDistanceKm={setShoppingListAlertMaxDistanceKm}
          setShoppingListAlertTargetTotal={setShoppingListAlertTargetTotal}
          setShoppingListText={setShoppingListText}
          shoppingListAlertMaxDistanceKm={shoppingListAlertMaxDistanceKm}
          shoppingListAlertTargetTotal={shoppingListAlertTargetTotal}
          shoppingListAlerts={shoppingListAlerts}
          shoppingListText={shoppingListText}
          shoppingResult={shoppingResult}
          toggleFavorite={toggleFavorite}
          t={t}
          userLocation={userLocation}
        />
      );
    }
    if (activeTab === "ranking") {
      return (
        <RankingPage
          currency={activeCurrency}
          distanceRanking={distanceRanking}
          loadMarketRankings={loadMarketRankings}
          openMarketPage={openMarketPage}
          ranking={localPriceRanking}
          t={t}
          visibleMarkets={visibleMarkets}
        />
      );
    }
    if (activeTab === "market") {
      return (
        <MarketPage
          goToMap={() => setActiveTab("map")}
          isLoadingMarketPrices={isLoadingMarketPrices}
          marketCatalogSyncMeta={marketCatalogSyncMeta}
          selectedMarket={selectedMarket}
          selectedMarketFavorite={selectedMarketFavorite}
          selectedMarketInsight={selectedMarketInsight}
          selectedMarketProduct={selectedMarketProduct}
          selectedMarketPrices={selectedMarketPrices}
          selectMarketProduct={setSelectedMarketProductName}
          t={t}
          toggleFavorite={toggleFavorite}
        />
      );
    }
    if (activeTab === "settings") {
      return (
        <SettingsPage
          activeDiscoveryRadiusLabel={configuredDiscoveryRadiusKmLabel}
          activeDiscoveryRadiusSecondaryLabel={configuredDiscoveryRadiusSecondaryLabel}
          activateSubscription={activateSubscription}
          adminDashboard={adminDashboard}
          analyticsOverview={analyticsOverview}
          alerts={alerts}
          billingOverview={billingOverview}
          deleteAccount={deleteAccount}
          deleteAccountForm={deleteAccountForm}
          defaultAddressId={defaultAddressId}
          defaultDiscoveryRadiusLabel={defaultDiscoveryRadiusLabel}
          deleteSavedAddress={deleteSavedAddress}
          currency={activeCurrency}
          deviceTokens={deviceTokens}
          distanceUnitLabel={distanceUnitLabel}
          favorites={favorites}
          isLocating={isLocating}
          isRequestingNotifications={isRequestingNotifications}
          isSavingSettings={isSavingSettings}
          isActivatingSubscription={isActivatingSubscription}
          isDeletingAccount={isDeletingAccount}
          markNotificationRead={markNotificationRead}
          locationPermission={locationPermission}
          logout={logout}
          newAddressLabel={newAddressLabel}
          notifications={notifications}
          shoppingListAlerts={shoppingListAlerts}
          removeAlert={removeAlert}
          removeShoppingListAlert={removeShoppingListAlert}
          removeFavorite={removeFavorite}
          notificationPermission={notificationPermission}
          profileForm={profileForm}
          refreshPermissionStates={refreshPermissionStates}
          requestCurrentLocation={requestCurrentLocation}
          requestNotificationAccess={requestNotificationAccess}
          saveCurrentAddress={saveCurrentAddress}
          saveUserSettings={saveUserSettings}
          setDefaultAddress={setDefaultAddress}
          setNewAddressLabel={setNewAddressLabel}
          secureContextAvailable={secureContextAvailable}
          selectedAddressLabel={selectedAddressLabel}
          suspiciousPrices={suspiciousPrices}
          language={language}
          languageOptions={LANGUAGE_OPTIONS}
          setDeleteAccountValue={setDeleteAccountValue}
          setLanguage={setLanguage}
          setProfileValue={setProfileValue}
          t={t}
          userProfile={userProfile}
          savedAddresses={savedAddresses}
        />
      );
    }
    return (
      <MapPage
        availableMarketCategories={availableMarketCategories}
        autoDiscoveredCount={autoDiscoveredCount}
        clearSelectedMarket={clearSelectedMarket}
        discoveryRadiusLabel={configuredDiscoveryRadiusKmLabel}
        distanceRanking={distanceRanking}
        handleMapPick={handleMapPick}
        handleViewportChange={handleViewportChange}
        isDiscoveringRadiusMarkets={isDiscoveringRadiusMarkets}
        isLoadingMarkets={isLoadingMarkets}
        isLocating={isLocating}
        loadMarketAroundUser={loadMarketAroundUser}
        mappedMarkets={categoryFilteredMarkets}
        openMarketPage={openMarketPage}
        previewMarketSelection={previewMarketSelection}
        radiusMarketCount={radiusMarketCount}
        requestCurrentLocation={requestCurrentLocation}
        selectedCategoryFilters={selectedCategoryFilters}
        selectedAddressLabel={selectedAddressLabel}
        selectedMarket={selectedMarket}
        selectedMarketId={selectedMarketId}
        selectedMarketInsight={selectedMarketInsight}
        selectedMapPoint={selectedMapPoint}
        t={t}
        toggleMapCategory={toggleMapCategory}
        userLocation={userLocation}
        visibleMarkets={visibleMarkets}
      />
    );
  };

  return (
    <div className="app-shell">
      <header className="global-topbar">
        <div className="topbar-left">
          {!isNavOpen ? (
            <button
              type="button"
              className="menu-toggle topbar-menu-toggle"
              onClick={() => setIsNavOpen(true)}
              aria-label={t("nav.open", "Abrir menu")}
            >
              <span />
              <span />
              <span />
            </button>
          ) : null}
        </div>

        <div className="topbar-center">
          <span className="topbar-brand">PennyPantry</span>
          <strong>{activeTabLabel}</strong>
        </div>

        <div className="topbar-right">
          <button
            type="button"
            className="topbar-account-button"
            aria-label={t("tab.settings", "Configuracoes")}
            onClick={() => selectTab("settings")}
          >
            <span className="topbar-account-label">{t("dashboard.hero.account", "Current account")}</span>
            <strong className="topbar-account-value">{topbarAccountLabel}</strong>
          </button>
        </div>
      </header>

      {isNavOpen ? (
        <button
          type="button"
          className="nav-backdrop"
          aria-label={t("nav.close", "Fechar menu")}
          onClick={() => setIsNavOpen(false)}
        />
      ) : null}

      <aside className={`side-nav ${isNavOpen ? "open" : ""}`} aria-label={t("app.nav.primary", "Navegacao principal")}>
        <div className="side-nav-header">
          <div>
            <div className="side-nav-kicker">PennyPantry</div>
            <strong>{t("nav.menu", "Menu")}</strong>
          </div>
          <button type="button" className="side-nav-close" onClick={() => setIsNavOpen(false)}>
            x
          </button>
        </div>
        <div className="side-nav-account">
          <span>{t("dashboard.hero.account", "Current account")}</span>
          <strong>{userProfile?.full_name || userProfile?.email || t("dashboard.hero.user", "User")}</strong>
          <small>
            {t("dashboard.hero.market_count", "{{count}} mercados visiveis no app", {
              count: mappedMarkets.length,
            })}
          </small>
        </div>
        <nav className="side-nav-list">
          {localizedTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`side-nav-button ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => selectTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </aside>

      <div className="mobile-frame">
        <div className="compact-location-card" style={surfaceStyle}>
          <span>
            {t("dashboard.hero.coverage", "Raio ativo: {{radius}}", {
              radius: configuredDiscoveryRadiusKmLabel,
            })}
          </span>
          <strong>
            {selectedAddressLabel ||
              (userLocation
                ? t("dashboard.hero.base_ready", "Location ready for live recommendations")
                : t("dashboard.hero.base_empty", "Defina uma localizacao"))}
          </strong>
        </div>

        {statusBanner}
        <SubscriptionPaywall
          billingOverview={billingOverview}
          isActivating={isActivatingSubscription}
          onActivate={activateSubscription}
          onLogout={logout}
          t={t}
        />
        {renderActivePage()}
      </div>
    </div>
  );
}
